import { createContext } from 'react';
import {
    faPersonWalkingArrowLoopLeft,
    faPersonWalkingArrowRight,
    faUserCheck,
    faUserClock,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';

const CommonContext = createContext();

export default CommonContext;

export const AppNavPaths = {
    ActiveEmployees: '/activeEmployees',
    AdminTool: '/adminTool',
    AssignmentConfirmation: '/confirm',
    AssignmentCalendar: '/assignmentCalendar',
    CalendarJobs: '/calendar/jobs',
    Billing: '/billing',
    BillingChargeCategory: '/billingChargeCategory',
    BillingCharge: '/billingCharge',
    BillingRate: '/billingRate',
    BillingIssues: '/billingIssues',
    DispatchIssues: '/dispatchIssues',
    CancelConfirmation: '/cancelConfirm',
    Certifications: '/certifications',
    ChargeView: '/chargeView',
    ChargeType: '/chargeType',
    ChargeTypeNew: '/newChargeType',
    ChargeTypes: '/chargeTypes',
    ChargeTypeUnits: '/chargeTypeUnit',
    ChargeTypeGroup: '/chargeTypeGroup',
    ChargeTypeGroupNew: '/newChargeTypeGroup',
    ChargeTypeGroups: '/chargeTypeGroups',
    Companies: '/companies',
    CollectedReporting: '/collectedReporting',
    Company: '/company',
    CompanyTypes: '/companyType',
    CompanyBillingGroup: '/companyBillingGroup',
    CompanyBillingGroupNew: '/companyBillingGroupNew',
    PaySchedule: '/paySchedule',
    PayScheduleNew: '/payScheduleNew',
    Contacts: '/contacts',
    Contract: '/contract',
    ContactActivities: '/contactActivities',
    ContractNew: '/newContract',
    Contracts: '/contracts',
    ContractStatuses: '/contractStatus',
    ContractTemplate: '/contractTemplate',
    ContractTemplateIndex: '/contractTemplates',
    ContractTemplateNew: '/newContractTemplate',
    DailyAttendanceEvents: '/dailyAttendanceEvents',
    EmployeeDailyRoster: '/roster',
    Dispatch: '/dispatch',
    DispatchPlus: '/dispatchPlus',
    DispatchPlusMap: '/dispatchPlus/map',
    DispatchPlusCallLog: '/dispatchPlus/call-log',
    DispatchPlusHandoff: '/dispatchPlus/handoff',
    DispatchPlusJobRequest: '/dispatchPlus/job-request',
    DispatchPlusDispatch: '/dispatchPlus/dispatch',
    DispatchGroups: '/dispatchGroups',
    DispatchGroup: '/dispatchGroup',
    DispatchGroupNew: '/dispatchGroupNew',
    Employee: '/employee',
    LongTermStatuses: '/longTermStatuses',
    EmployeeNew: '/newEmployee',
    EmployeeNewHire: '/newEmployee?newHire=true',
    EmployeeAccountStatuses: '/employeeAccountStatus',
    Employees: '/employees',
    EmployeeTimesheets: '/myTimesheets',
    Equipment: '/equipment',
    EquipmentStatuses: '/equipmentStatuses',
    EquipmentTypes: '/equipmentTypes',
    EquipmentTypeStatuses: '/equipmentTypeStatuses',
    Estimates: '/estimates',
    Estimate: '/estimate',
    Group: '/group',
    Groups: '/groups',
    GroupNew: '/newGroup',
    Invoice: '/invoice',
    RentalInvoice: '/rentalInvoice',
    InvoiceGeneration: '/invoiceGeneration',
    NotFound: '/notFound',
    NoPermission: '/noPermission',
    Unauthorized: '/unauthorized',
    UserGroup: '/userGroup',
    UserGroups: '/userGroups',
    UserGroupNew: '/newUserGroup',
    Role: '/role',
    Roles: '/roles',
    RoleNew: '/newRole',
    Permission: '/permission',
    Permissions: '/permissions',
    PermissionNew: '/newPermission',
    Holidays: '/holidays',
    Job: '/job',
    JobNew: '/newJob',
    JobView: '/jobView',
    Jobs: '/jobs',
    JobAssignmentCancellationTypes: '/jobAssignmentCancellationTypes',
    JobTypes: '/jobTypes',
    Organization: '/organization',
    OrganizationNew: '/newOrganization',
    Organizations: '/organizations',
    LocationTypes: '/locationTypes',
    Payroll: '/payroll',
    PayrollIndividual: '/payrollIndividual',
    QuickBooksInventoryItems: '/quickBooksInventoryItems',
    QuickBooksInventoryItem: '/quickBooksInventoryItem',
    QuickBooksInventoryItemNew: '/quickBooksInventoryItem/new',
    SelfDispatchTimesheet: '/sdtimesheet',
    SelfDispatchTimesheets: '/sdtimesheets',
    SelfDispatchTimesheetNew: '/sdtimesheetNew',
    NonFlaggingTimesheet: '/nonFlagTimesheet',
    NonFlaggingTimesheetNew: '/nonFlagTimesheetNew',
    CrewTimesheet: '/crewTimesheet',
    ServerError: '/servererror',
    PayrollManagement: '/payrollManagement',
    Tenant: '/tenant',
    Tenants: '/tenants',
    TimeCharges: '/timecharges',
    TimeOffRequests: '/timeoff',
    Timesheet: '/timesheet',
    TimesheetManagement: '/timesheetManagement',
    USCountyGroups: '/countyGroups',
    USCountyGroupNew: '/countyGroup/new',
    USCountyGroup: '/countyGroup',
    USStates: '/states',
    USStateNew: '/states/new',
    USState: '/state',
    JotForms: '/jotForms',
    JotFormNew: '/jotForm/new',
    JotForm: '/jotForm',
    JotFormThankYou: '/thankyou',
    Project: '/project',
    ProjectNew: '/newProject',
    ProjectView: '/projectView',
    Projects: '/projects',
    EquipmentTags: '/equipmentTags',
    EquipmentTagNew: '/equipmentTag/new',
    EquipmentTag: '/equipmentTag',
    OperationalCategories: '/operationalCategories',
    OperationalCategoryNew: '/operationalCategory/new',
    OperationalCategory: '/operationalCategory',
    Delegates: '/delegates',
    DelegateNew: '/newDelegate',
    Delegate: '/delegate',
    QuickSales: '/quickSale',
    ResourceKits: '/resourceKits',
    ResourceKitNew: '/resourceKitNew',
    ResourceKit: '/resourceKit',
    TimeTracker: '/timeTracker',
    CallDispositions: '/callDispositions',
    SupportTools: '/supportTools',
};

export const YesNoOptions = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
];

export const CustomFormContext = {
    Job: 1,
    QuickSale: 2,
    Invoice: 3,
    Timesheet: 4,
};

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
export const isValidGuid = (id) => !!id && id !== EMPTY_GUID;

// Which of a timesheet's attachments a call works with — mirrors TimesheetAttachmentScope server side.
export const TimesheetAttachmentScope = {
    All: 'All',
    PaintBatchPhotos: 'PaintBatchPhotos',
    Documents: 'Documents',
};

const prefix = '/api/v1';
export const ApiRoutes = {
    activeEmployees: {
        query: () => `${prefix}/activeEmployees/query`,
        export: () => `${prefix}/activeEmployees/export`,
    },
    address: {
        create: () => `${prefix}/address`,
        update: (addressId) => `${prefix}/address/${addressId}`,
    },
    adminTool: {
        search: () => `${prefix}/admintools/search`,
        getAdminToolHistory: (id) => `${prefix}/admintoolHistory/getAdminToolHistory/${id}`,
        moveTimesheetToJob: () => `${prefix}/admintool/moveTimesheetToJob`,
        changeJobDateAndTime: () => `${prefix}/admintool/changeJobDateAndTime`,
        duplicateTimesheet: () => `${prefix}/admintool/duplicateTimesheet`,
        swapContractChargeType: () => `${prefix}/admintool/swapContractChargeType`,
        syncTimesheetContractBilling: () => `${prefix}/admintool/syncTimesheetContractBilling`,
        addJobCity: () => `${prefix}/admintool/addJobCity`,
        crewEtaTestDriveStart: () => `${prefix}/admintool/crewEtaTestDrive`,
        crewEtaTestDriveEnd: () => `${prefix}/admintool/crewEtaTestDrive/end`,
        testSms: () => `${prefix}/admintool/testSms`,
        testEmail: () => `${prefix}/admintool/testEmail`,
        ManageGroupHoliday: () => `${prefix}/admintool/manageGroupHoliday`,
        grantAdminAccess: () => `${prefix}/admintool/grantAdminAccess`,
        copyTenantData: () => `${prefix}/admintool/copyTenantData`,
        grantMultiTenantAccess: () => `${prefix}/admintool/grantMultiTenantAccess`,
        importCustomerData: () => `${prefix}/admintool/importCustomerData`,
        getImportFile: (historyId) => `${prefix}/admintool/getImportFile/${historyId}`,
        addChargesToTimesheet: () => `${prefix}/admintool/addChargesToTimesheet`,
        rejectBillingPayroll: () => `${prefix}/admintool/rejectBillingPayroll`,
        removeCharges: () => `${prefix}/admintool/removeCharges`,
        moveTimesheetToContract: () => `${prefix}/admintool/moveTimesheetToContract`,
        addChargeTypeToContracts: () => `${prefix}/admintool/addChargeTypeToContracts`,
        upsertHolidayGroup: () => `${prefix}/admintool/upsertHolidayGroup`,
        mergeProjects: () => `${prefix}/admintool/mergeProjects`,
        movableJobs: (projectId) => `${prefix}/admintool/movableJobs/${projectId}`,
        ManageChargeQuestion: (chargeTypeId) => `${prefix}/admintool/manageChargeQuestion/${chargeTypeId}`,
        rollbackPayrollExport: () => `${prefix}/admintool/rollbackPayrollExport`,
    },
    auth: {
        permissions: () => `${prefix}/authorization/permissions`,
        profile: () => `${prefix}/authorization/profile`,
        roles: () => `${prefix}/authorization/roles`,
        groups: () => `${prefix}/authorization/groups`,
        groupsByTenantId: (tenantId) => `${prefix}/authorization/groupsByTenantId/${tenantId}`,
        confirmPhone: (code) => `${prefix}/authorization/confirmPhone/${code}`,
        updateTenant: (tenantId) => `${prefix}/authorization/updateTenant/${tenantId}`,
        setDefaultTenant: (tenantId) => `${prefix}/authorization/setDefaultTenant/${tenantId}`,
        updateToken: () => `${prefix}/authorization/updateToken`,
        updateEmail: () => `${prefix}/authorization/updateEmail`,
        updateFlaggerOnCallStatus: () => `${prefix}/authorization/updateFlaggerOnCallStatus`,
        updateDispatchPlusNotificationSubscription: () => `${prefix}/authorization/updateDispatchPlusNotificationSubscription`,
    },
    billing: {
        search: () => `${prefix}/billings/search`,
        excelExport: () => `${prefix}/billings/excelExport`,
        export: () => `${prefix}/billings/export`,
        approve: () => `${prefix}/billings/approve`,
        reject: () => `${prefix}/billings/reject`,
        adminReject: () => `${prefix}/billings/adminReject`,
        needsAdminReject: (id) => `${prefix}/billings/needsAdminReject/${id}`,
        download: (id) => `${prefix}/billing/download/${id}`,
        setEmergency: () => `${prefix}/billing/setEmergency`,
    },
    billingChargeCategory: {
        edit: () => `${prefix}/billingChargeCategory/edit`,
        search: () => `${prefix}/billingChargeCategories/search`,
        excelExport: () => `${prefix}/billingChargeCategories/excelExport`,
        export: () => `${prefix}/billingChargeCategories/export`,
        approve: () => `${prefix}/billingChargeCategories/approve`,
        reject: () => `${prefix}/billingChargeCategories/reject`,
        process: () => `${prefix}/billingChargeCategories/process`,
        download: (id) => `${prefix}/billingChargeCategory/download/${id}`,
        setEmergency: () => `${prefix}/billingChargeCategory/setEmergency`,
        slideout: (billingId) => `${prefix}/billingChargeCategory/${billingId}/slideout`,
        billperiods: (billingId) => `${prefix}/billingChargeCategory/${billingId}/billperiods`,
    },
    billingCharge: {
        add: () => `${prefix}/billingCharge/add`,
        edit: () => `${prefix}/billingCharge/edit`,
        search: () => `${prefix}/billingCharges/search`,
        excelExport: () => `${prefix}/billingCharges/excelExport`,
        export: () => `${prefix}/billingCharges/export`,
        approve: () => `${prefix}/billingCharges/approve`,
        reject: () => `${prefix}/billingCharges/reject`,
        forceReject: () => `${prefix}/billingCharges/forceReject`,
        adminReject: () => `${prefix}/billingCharges/adminReject`,
        needsAdminReject: (id) => `${prefix}/billingCharges/needsAdminReject/${id}`,
        process: () => `${prefix}/billingCharges/process`,
        download: (id) => `${prefix}/billingCharge/download/${id}`,
        setEmergency: () => `${prefix}/billingCharge/setEmergency`,
        slideout: (billingId) => `${prefix}/billingCharge/${billingId}/slideout`,
        unapprove: (billingDetailId) => `${prefix}/billingCharge/unapprove/${billingDetailId}`,
        void: () => `${prefix}/billingCharge/void`,
        unvoid: (billingId) => `${prefix}/billingCharge/unvoid/${billingId}`,
        needsForceReject: (id) => `${prefix}/billingCharges/needsForceReject/${id}`,
    },
    billingRate: {
        search: () => `${prefix}/billingRates/search`,
        getGridPermissions: () => `${prefix}/billingRates/getGridPermissions`,
        excelExport: () => `${prefix}/billingRates/excelExport`,
        chargeTypeBillingRateHistory: (chargeTypeId) => `${prefix}/billingRates/chargeTypeBillingRateHistory/${chargeTypeId}`,
        companyBillingRateHistory: () => `${prefix}/billingRates/companyBillingRateHistory`,
    },
    billingIssues: {
        search: () => `${prefix}/billingIssues/search`,
        reject: () => `${prefix}/billingIssues/reject`,
        invoiceReject: () => `${prefix}/billingIssues/invoiceReject`,
        accept: () => `${prefix}/billingIssues/accept`,
        invoiceAccept: () => `${prefix}/billingIssues/invoiceAccept`,
        reply: () => `${prefix}/billingIssues/reply`,
        messages: (billingId) => `${prefix}/billingIssues/${billingId}`,
        invoiceMessages: () => `${prefix}/billingIssues/invoiceMessages`,
        excelExport: () => `${prefix}/billingIssues/excelExport`,
    },
    dispatchFlag: {
        flag: () => `${prefix}/dispatchFlag/flag`,
        resolve: (flagId) => `${prefix}/dispatchFlag/resolve/${flagId}`,
        search: () => `${prefix}/dispatchFlag/search`,
        types: () => `${prefix}/dispatchFlag/types`,
        forJobs: () => `${prefix}/dispatchFlag/forjobs`,
    },
    calendar: {
        get: (start, end) => `${prefix}/calendar/${start}/${end}`,
    },
    calendarJobs: `${prefix}/calendarJobs`,
    chargeView: {
        search: () => `${prefix}/chargeViews/search`,
        excelExport: () => `${prefix}/chargeViews/excelExport`,
        hoursReportCompanies: () => `${prefix}/chargeViews/hoursReportCompanies`,
        hoursReportExport: () => `${prefix}/chargeViews/hoursReportExport`,
        billingReportExport: () => `${prefix}/chargeViews/billingReportExport`,
    },
    chargeType: {
        all: () => `${prefix}/chargeTypes`,
        search: () => `${prefix}/chargeTypes/search`,
        byCustomer: (customerId) => `${prefix}/chargeTypes/customer${customerId ? `/${customerId}` : ''}`,
        byCustomerBillable: (customerId) => `${prefix}/chargeTypes/customerBillable${customerId ? `/${customerId}` : ''}`,
        byId: (chargeTypeId) => `${prefix}/chargeType/${chargeTypeId}`,
        create: () => `${prefix}/chargeType`,
        update: (chargeTypeId) => `${prefix}/chargeType/${chargeTypeId}`,
        delete: (chargeTypeId) => `${prefix}/chargeType/${chargeTypeId}`,
        byContract: (id) => `${prefix}/chargeTypes/contract/${id}`,
        bySetupTypes: () => `${prefix}/chargeTypes/bySetupTypes`,
        getSurcharges: () => `${prefix}/chargeTypes/getSurcharges`,
        byTimesheetNumber: (timesheetNum) => `${prefix}/chargeTypes/byTimesheet/${timesheetNum}`,
        byTimesheetId: (timesheetId) => `${prefix}/chargeTypes/byTimesheetId/${timesheetId}`,
        byContractId: (contractId) => `${prefix}/chargeTypes/byContractId/${contractId}`,
        byIds: () => `${prefix}/chargeTypes/byIds`,
        nonSurcharges: () => `${prefix}/chargeTypes/nonSurcharges`,
    },
    chargeTypeStatus: {
        all: () => `${prefix}/chargeTypeStatuses`,
        search: () => `${prefix}/chargeTypeStatuses`,
        byId: (chargeTypeStatusId) => `${prefix}/chargeTypeStatus/${chargeTypeStatusId}`,
        create: () => `${prefix}/chargeTypeStatus`,
        update: (chargeTypeStatusId) => `${prefix}/chargeTypeStatus/${chargeTypeStatusId}`,
        delete: (chargeTypeStatusId) => `${prefix}/chargeTypeStatus/${chargeTypeStatusId}`,
    },
    chargeTypeUnit: {
        all: () => `${prefix}/chargeTypeUnits`,
        search: () => `${prefix}/chargeTypeUnits/search`,
        byId: (chargeTypeUnitId) => `${prefix}/chargeTypeUnit/${chargeTypeUnitId}`,
        create: () => `${prefix}/chargeTypeUnit`,
        update: (chargeTypeUnitId) => `${prefix}/chargeTypeUnit/${chargeTypeUnitId}`,
        delete: (chargeTypeUnitId) => `${prefix}/chargeTypeUnit/${chargeTypeUnitId}`,
    },
    payrollChargeType: {
        all: () => `${prefix}/payrollChargeType`,
        search: () => `${prefix}/payrollChargeType/search`,
        byId: (payrollChargeTypeId) => `${prefix}/payrollChargeType/${payrollChargeTypeId}`,
        create: () => `${prefix}/payrollChargeType`,
        update: (payrollChargeTypeId) => `${prefix}/payrollChargeType/${payrollChargeTypeId}`,
        delete: (payrollChargeTypeId) => `${prefix}/payrollChargeType/${payrollChargeTypeId}`,
    },
    chargeTypeGroup: {
        search: () => `${prefix}/chargeTypeGroups/search`,
        byId: (chargeTypeGroupId) => `${prefix}/chargeTypeGroup/${chargeTypeGroupId}`,
        create: () => `${prefix}/chargeTypeGroup`,
        update: (chargeTypeGroupId) => `${prefix}/chargeTypeGroup/${chargeTypeGroupId}`,
        delete: (chargeTypeGroupId) => `${prefix}/chargeTypeGroup/${chargeTypeGroupId}`,
        byContract: (id, usCountyId) => `${prefix}/chargeTypeGroups/contract/${id}${usCountyId ? `/${usCountyId}` : ''}`,
        byContractAndJobType: (id, jobTypeId) => `${prefix}/chargeTypeGroups/contractJobType/${id}/${jobTypeId}}`,
    },
    certification: {
        search: () => `${prefix}/certification/search`,
        update: (certificationId) => `${prefix}/certification/${certificationId}`,
        create: () => `${prefix}/certification/create`,
        byId: (certificationId) => `${prefix}/certification/${certificationId}`,
        all: () => `${prefix}/certification`,
        excelExport: () => `${prefix}/certification/excelExport`,
    },
    contractCertification: {
        byId: (contractId) => `${prefix}/contract/${contractId}`,
    },
    company: {
        all: () => `${prefix}/companies`,
        create: () => `${prefix}/company`,
        createQuickCustomer: () => `${prefix}/company/createQuickCustomer`,
        invoicing: () => `${prefix}/companies/invoicing`,
        byId: (companyId) => `${prefix}/company/${companyId}`,
        locations: (companyId) => `${prefix}/company/${companyId}/locations`,
        search: () => `${prefix}/companies/search`,
        excelExport: () => `${prefix}/companies/excelExport`,
        types: () => `${prefix}/company/types`,
        customerCompanies: (companyId) => `${prefix}/companies/customers/${companyId}`,
        customerOperationsCentersCompanies: (customerCompanyId) => `${prefix}/companies/customerOperationCenters/${customerCompanyId}`,
        payrollCompanies: () => `${prefix}/companies/payroll`,
        dispatchCompanies: () => `${prefix}/companies/dispatch`,
        dispatchCompaniesByCompanyId: (companyId) => `${prefix}/companies/dispatch/${companyId}`,
        foremen: (companyId) => `${prefix}/company/${companyId}/foremen`,
        contacts: (companyId) => `${prefix}/company/${companyId}/contacts`,
        contactDetails: (companyId) => `${prefix}/company/${companyId}/contactDetails`,
        allowAlternativePayroll: (companyId) => `${prefix}/company/${companyId}/allowAlternativePayroll`,
        operationsCentersApply: (companyId) => `${prefix}/company/${companyId}/operationsCentersApply`,
        defaultTerms: (companyId) => `${prefix}/company/defaultTerms/${companyId}`,
        defaultMapping: (companyId) => `${prefix}/company/defaultMapping/${companyId}`,
        defaultChargeTypeGroups: (companyId) => `${prefix}/companies/defaultChargeTypeGroups/${companyId}`,
        isIntercompany: (companyId) => `${prefix}/company/${companyId}/isIntercompany`,
    },
    companyBillingGroups: {
        search: () => `${prefix}/companies/searchBillingGroups`,
        byId: (billingGroupId) => `${prefix}/company/billingGroup/${billingGroupId}`,
        create: () => `${prefix}/company/billingGroup`,
        update: (billingGroupId) => `${prefix}/company/billingGroup/${billingGroupId}`,
    },
    companyType: {
        search: () => `${prefix}/companyTypes/search`,
        byId: (companyTypeId) => `${prefix}/companyType/${companyTypeId}`,
        create: () => `${prefix}/companyType`,
        update: (companyTypeId) => `${prefix}/companyType/${companyTypeId}`,
        delete: (companyTypeId) => `${prefix}/companyType/${companyTypeId}`,
    },
    contactActivity: {
        byId: (contactActivityId) => `${prefix}/contactActivity/${contactActivityId}`,
        update: (contactActivityId) => `${prefix}/contactActivity/${contactActivityId}`,
        create: () => `${prefix}/contactActivity`,
        search: () => `${prefix}/contactActivities/search`,
        delete: (contactActivityId) => `${prefix}/contactActivity/${contactActivityId}`,
        excelExport: () => `${prefix}/contactActivities/excelExport`,
    },
    contract: {
        byId: (contractId) => `${prefix}/contract/${contractId}`,
        filteredRates: (contractId, dateStr, countyId) => {
            let baseUrl = `${prefix}/contract/${contractId}/${dateStr}`;
            if (countyId != null) {
                baseUrl += `?countyId=${encodeURIComponent(countyId)}`;
            }
            return baseUrl;
        },
        update: (contractId) => `${prefix}/contract/${contractId}`,
        create: () => `${prefix}/contract`,
        search: () => `${prefix}/contracts/search`,
        searchExport: () => `${prefix}/contracts/searchExport`,
        getGridPermissions: () => `${prefix}/contracts/getGridPermissions`,
        userHasSelfDispatching: () => `${prefix}/contracts/hasSelfDispatching`,
        getNonFlagging: () => `${prefix}/contracts/getNonFlagging`,
        getSelfDispatching: () => `${prefix}/contracts/selfDispatching`,
        moveTimesheets: () => `${prefix}/contract/moveTimesheets`,
        refreshChargeTypes: (contractId) => `${prefix}/contract/refreshChargeTypes/${contractId}`,
        changeContractNumber: () => `${prefix}/contract/changeContractNumber`,
        progress: (contractId) => `${prefix}/contract/progress/${contractId}`,
        createQuickContract: () => `${prefix}/contract/createQuickContract`,
    },
    contractHistory: {
        byId: (contractId) => `${prefix}/contractHistory/${contractId}`,
    },
    contractTemplate: {
        create: () => `${prefix}/contractTemplate`,
        byId: (id) => `${prefix}/contractTemplate/${id}`,
        search: () => `${prefix}/contractTemplates/search`,
        names: () => `${prefix}/contractTemplates`,
    },
    contractStatus: {
        search: () => `${prefix}/contractStatuses/search`,
        byId: (contractStatusId) => `${prefix}/contractStatus/${contractStatusId}`,
        create: () => `${prefix}/contractStatus`,
        update: (contractStatusId) => `${prefix}/contractStatus/${contractStatusId}`,
        delete: (contractStatusId) => `${prefix}/contractStatus/${contractStatusId}`,
    },
    customers: {
        all: (companyId) => `${prefix}/customers/${companyId}`,
    },
    dailyAttendanceEvent: {
        all: () => `${prefix}/dailyAttendanceEvents`,
        byId: (id) => `${prefix}/dailyAttendanceEvent/${id}`,
        create: () => `${prefix}/dailyAttendanceEvent/create`,
        update: () => `${prefix}/dailyAttendanceEvent/update`,
        search: () => `${prefix}/dailyAttendanceEvents/search`,
        delete: (id) => `${prefix}/dailyAttendanceEvent/${id}`,
    },
    dispatch: {
        viewModel: () => `${prefix}/dispatch/viewmodel`,
        staff: () => `${prefix}/dispatch/staff`,
        equipment: () => `${prefix}/dispatch/equipment`,
        crews: () => `${prefix}/dispatch/crews`,
        jobs: () => `${prefix}/dispatch/jobs`,
        cancelledJobs: () => `${prefix}/dispatch/cancelledJobs`,
        saveAssignment: () => `${prefix}/dispatch/saveAssignment`,
        removeAssignment: (jobAssignmentId) => `${prefix}/dispatch/removeAssignment/${jobAssignmentId}`,
        removeAllAssignments: () => `${prefix}/dispatch/removeAllAssignments`,
        cancelAssignment: () => `${prefix}/dispatch/cancelAssignment`,
        changeCancellationType: () => `${prefix}/dispatch/changeCancellationType`,
        replaceAssignment: () => `${prefix}/dispatch/replaceAssignment`,
        cancelJob: () => `${prefix}/dispatch/cancelJob`,
        transferJobDay: () => `${prefix}/dispatch/transferJobDay`,
        setCrewLead: (jobAssignmentId) => `${prefix}/dispatch/crewLead/${jobAssignmentId}`,
        setEquipmentAssignedEmployee: (jobAssignmentId, employeeId) => `${prefix}/dispatch/setEquipmentAssignedEmployee/${jobAssignmentId}/${employeeId}`,
        setEquipmentAssignedEquipment: (jobAssignmentId, equipmentId) => `${prefix}/dispatch/setEquipmentAssignedEquipment/${jobAssignmentId}/${equipmentId}`,
        crossDispatchJobs: () => `${prefix}/dispatch/crossDispatchJobs`,
        resourcesForJobDate: () => `${prefix}/dispatch/resourcesForJobDate`,
        autoAssign: () => `${prefix}/dispatch/autoAssign`,
    },
    dispatchPlus: {
        handoff: (dispatchLocationId, date) => `${prefix}/dispatchPlus/handoff/${dispatchLocationId}?date=${date}`,
        submitHandoff: () => `${prefix}/dispatchPlus/handoff`,
        mapData: () => `${prefix}/dispatchPlus/mapData`,
    },
    dispatchGroup: {
        search: () => `${prefix}/dispatchGroup/search`,
        byId: (dispatchGroupId) => `${prefix}/dispatchGroup/${dispatchGroupId}`,
        create: () => `${prefix}/dispatchGroup`,
    },
    employee: {
        toggleImpersonation: () => `${prefix}/employee/toggleImpersonation`,
        suggestUsername: (name) => `${prefix}/employee/suggestUsername/${name}`,
        byId: (empId) => `${prefix}/employee/${empId}`,
        metrics: (empId) => `${prefix}/employee/${empId}/metrics`,
        create: () => `${prefix}/employee`,
        update: (empId) => `${prefix}/employee/${empId}`,
        unlock: (empId) => `${prefix}/employee/${empId}/unlock`,
        payRate: {
            create: (empId) => `${prefix}/employee/${empId}/payRate`,
            update: (empId, payRateId) => `${prefix}/employee/${empId}/payRate/${payRateId}`,
        },
        groups: {
            all: () => `${prefix}/employees`,
        },
        rosterView: (empId) => `${prefix}/employee/${empId}/roster`,
    },
    employees: {
        all: () => `${prefix}/employees`,
        associatedWithContactActivity: () => `${prefix}/employees/associatedWithContactActivity`,
        byCompany: (companyId) => `${prefix}/employees/${companyId}`,
        byDispatchLocation: (dispatchLocationId) => `${prefix}/employees/byDispatchLocation/${dispatchLocationId}`,
        byDispatchLocationAndEquipmentType: (
            dispatchLocationId,
            equipmentTypeId,
        ) => `${prefix}/employees/byDispatchLocationAndEquipmentType/${dispatchLocationId}/${equipmentTypeId}`,
        search: () => `${prefix}/employees/search`,
        roster: () => `${prefix}/employees/roster`,
        rosterExport: () => `${prefix}/employees/rosterExport`,
        excelExport: () => `${prefix}/employees/excelExport`,
        fetchEmployeesForMap: () => `${prefix}/employees/fetchEmployeesForMap`,
        certificationReportExport: () => `${prefix}/employees/certificationReportExport`,
    },

    employeeAccountStatus: {
        byId: (employeeAccountStatusId) => `${prefix}/employeeAccountStatus/${employeeAccountStatusId}`,
        create: () => `${prefix}/employeeAccountStatus`,
        update: (employeeAccountStatusId) => `${prefix}/employeeAccountStatus/${employeeAccountStatusId}`,
        delete: (employeeAccountStatusId) => `${prefix}/employeeAccountStatus/${employeeAccountStatusId}`,
    },
    employeeAccountStatuses: {
        search: () => `${prefix}/employeeAccountStatuses/search`,
        all: () => `${prefix}/employeeAccountStatuses`,
    },
    employeeDailyAttendanceEvent: {
        byId: (id) => `${prefix}/employeeDailyAttendanceEvent/${id}`,
        upsert: () => `${prefix}/employeeDailyAttendanceEvent/upsert`,
        delete: () => `${prefix}/employeeDailyAttendanceEvent/delete`,
        allFor: () => `${prefix}/employeeDailyAttendanceEvents/allFor`,
    },
    employeeLongTermStatus: {
        allForEmployee: (employeeId) => `${prefix}/employeeLongTermStatuses/${employeeId}`,
        byId: (id) => `${prefix}/employeeLongTermStatus/${id}`,
        create: () => `${prefix}/employeeLongTermStatus/create`,
        update: () => `${prefix}/employeeLongTermStatus/update`,
        delete: (id) => `${prefix}/employeeLongTermStatus/${id}`,
        allEffectiveBetween: () => `${prefix}/employeeLongTermStatuses/allEffectiveBetween`,
    },
    employeeCertification: {
        allForEmployee: (employeeId) => `${prefix}/employeeCertifications/employee/${employeeId}`,
        create: () => `${prefix}/employeeCertifications/create`,
        byId: (id) => `${prefix}/employeeCertifications/${id}`,
        update: (id) => `${prefix}/employeeCertifications/${id}`,
        delete: (id) => `${prefix}/employeeCertifications/${id}`,
        download: (id) => `${prefix}/employeeCertifications/download/${id}`,
    },
    employeeRestriction: {
        allForEmployee: (employeeId) => `${prefix}/employeeRestriction/employee/${employeeId}`,
        byId: (id) => `${prefix}/employeeRestriction/${id}`,
        create: () => `${prefix}/employeeRestriction/create`,
        update: () => `${prefix}/employeeRestriction/update`,
        delete: (id) => `${prefix}/employeeRestriction/${id}`,
    },

    employeeHistory: {
        byId: (empId) => `${prefix}/employeeHistory/${empId}`,
    },
    companyHistory: {
        byId: (companyId) => `${prefix}/companyHistory/${companyId}`,
    },
    invoiceHistory: {
        byId: (invoiceId) => `${prefix}/invoiceHistory/${invoiceId}`,
    },
    equipment: {
        all: () => `${prefix}/equipments`,
        search: () => `${prefix}/equipments/search`,
        excelExport: () => `${prefix}/equipments/excelExport`,
        byId: (equipmentId) => `${prefix}/equipment/${equipmentId}`,
        byDispatchLocation: (companyId) => `${prefix}/equipments/company/${companyId}`,
        byType: (typeId) => `${prefix}/equipments/${typeId}`,
        create: () => `${prefix}/equipment`,
        update: (equipmentId) => `${prefix}/equipment/${equipmentId}`,
        delete: (equipmentId) => `${prefix}/equipment/${equipmentId}`,
        trucksReport: () => `${prefix}/equipments/trucksReport`,
        roster: () => `${prefix}/equipments/roster`,
        rosterExport: () => `${prefix}/equipments/rosterExport`,
        rosterView: (equipmentId) => `${prefix}/equipment/${equipmentId}/roster`,
        refreshNetSuiteAssets: () => `${prefix}/equipments/refreshNetSuiteAssets`,
    },
    equipmentLongTermStatus: {
        allForEquipment: (equipmentId) => `${prefix}/equipmentLongTermStatuses/${equipmentId}`,
        byId: (id) => `${prefix}/equipmentLongTermStatus/${id}`,
        create: () => `${prefix}/equipmentLongTermStatus/create`,
        update: () => `${prefix}/equipmentLongTermStatus/update`,
        delete: (id) => `${prefix}/equipmentLongTermStatus/${id}`,
        allEffectiveBetween: () => `${prefix}/equipmentLongTermStatuses/allEffectiveBetween`,
    },
    equipmentType: {
        all: () => `${prefix}/equipmentTypes`,
        search: () => `${prefix}/equipmentTypes/search`,
        byId: (equipmentTypeId) => `${prefix}/equipmentType/${equipmentTypeId}`,
        create: () => `${prefix}/equipmentType`,
        update: (equipmentTypeId) => `${prefix}/equipmentType/${equipmentTypeId}`,
        delete: (equipmentTypeId) => `${prefix}/equipmentType/${equipmentTypeId}`,
    },
    equipmentStatus: {
        all: () => `${prefix}/equipmentStatuses`,
        search: () => `${prefix}/equipmentStatuses/search`,
        byId: (equipmentStatusId) => `${prefix}/equipmentStatus/${equipmentStatusId}`,
        create: () => `${prefix}/equipmentStatus`,
        update: (equipmentStatusId) => `${prefix}/equipmentStatus/${equipmentStatusId}`,
        delete: (equipmentStatusId) => `${prefix}/equipmentStatus/${equipmentStatusId}`,
    },
    equipmentTypeStatus: {
        all: () => `${prefix}/equipmentTypeStatuses`,
        search: () => `${prefix}/equipmentTypeStatuses/search`,
        byId: (equipmentTypeId) => `${prefix}/equipmentTypeStatus/${equipmentTypeId}`,
        create: () => `${prefix}/equipmentTypeStatus`,
        update: (equipmentTypeId) => `${prefix}/equipmentTypeStatus/${equipmentTypeId}`,
        delete: (equipmentTypeId) => `${prefix}/equipmentTypeStatus/${equipmentTypeId}`,
    },
    estimates: {
        search: () => `${prefix}/estimates/search`,
    },
    estimate: {
        lineItems: (salesOrderId) => `${prefix}/estimate/lineItems/${salesOrderId}`,
    },
    group: {
        all: () => `${prefix}/groups`,
        search: () => `${prefix}/groups/search`,
        byId: (groupId) => `${prefix}/group/${groupId}`,
        create: () => `${prefix}/group`,
        update: (groupId) => `${prefix}/group/${groupId}`,
        delete: (groupId) => `${prefix}/group/${groupId}`,
    },
    holiday: {
        all: ({ getAllHoliday = false, tenantId = 0 } = {}) => {
            const base = `${prefix}/holidays/getAvailableHolidays`;
            const params = [];
            if (getAllHoliday) params.push('getAllHoliday=true');
            if (tenantId) params.push(`tenantId=${tenantId}`);
            return params.length ? `${base}?${params.join('&')}` : base;
        },
        companyHolidays: (companyId) => `${prefix}/holidays/companyHolidays/${companyId}`,
        countyGroupHolidays: (countyGroupId) => `${prefix}/holidays/countyGroupHolidays/${countyGroupId}`,
        createCompanyHolidays: (companyId) => `${prefix}/holidays/createCompanyHolidays/${companyId}`,
        createCountyGroupHolidays: (countyGroupId) => `${prefix}/holidays/createCountyGroupHolidays/${countyGroupId}`,
        createHolidayGroupHolidays: (tenantId) => `${prefix}/holidays/createHolidayGroupHolidays/${tenantId}`,
        holidayGroupSearch: () => `${prefix}/holidays/holidayGroups`,
        listGroupHolidays: (groupTypeId, tenantId) =>
            `${prefix}/holidays/groupHolidays/${groupTypeId}?tenantId=${tenantId}`,
        tenantHolidays: (tenantId) => `${prefix}/holidays/tenantHolidays/${tenantId}`,
    },
    invoice: {
        streamCTRFile: (invoiceId) => `${prefix}/invoice/streamCTRFile/${invoiceId}`,
        streamProofOf40File: (invoiceId) => `${prefix}/invoice/streamProofOf40File/${invoiceId}`,
        streamInvoice: (invoiceId) => `${prefix}/invoice/streamInvoice/${invoiceId}`,
        streamInvoiceTimesheets: (invoiceId) => `${prefix}/invoice/streamInvoiceTimesheets/${invoiceId}`,
        streamInvoiceWorkCards: (ids) => `${prefix}/invoices/streamInvoiceWorkCards/${ids}`,
        downloadInvoiceWorkCards: (ids) => `${prefix}/invoices/downloadInvoiceWorkCards/${ids}`,
        draft: (invoiceId) => `${prefix}/invoice/draft/${invoiceId}`,
        draftTimesheetDelete: () => `${prefix}/invoice/draftTimesheetDelete`,
        approve: (invoiceId) => `${prefix}/invoice/approve/${invoiceId}`,
        revert: (invoiceId) => `${prefix}/invoice/revert/${invoiceId}`,
        create: () => `${prefix}/invoice`,
        update: (invoiceId) => `${prefix}/invoice/${invoiceId}`,
        restart: (invoiceId) => `${prefix}/invoice/restart/${invoiceId}`,
        delete: (invoiceId) => `${prefix}/invoice/${invoiceId}`,
        byId: (invoiceId) => `${prefix}/invoice/${invoiceId}`,
        search: (rentalInvoices) => `${prefix}/invoices/search/${rentalInvoices}`,
        timesheets: (contractId, workorderNumber, weekDate) => `${prefix}/invoices/timesheets/${contractId}/${workorderNumber}/${weekDate}`,
        ctrexport: (id) => `${prefix}/invoice/ctrexport/${id}`,
        proofof40: (id) => `${prefix}/invoice/proofof40/${id}`,
        downloadInvoiceTimesheets: (id) => `${prefix}/invoice/downloadInvoiceTimesheets/${id}`,
        emailInvoice: (id) => `${prefix}/invoice/emailInvoice/${id}`,
        downloadInvoice: (id) => `${prefix}/invoice/downloadInvoice/${id}`,
        streamInvoices: (ids) => `${prefix}/invoices/streamInvoices/${ids}`,
        exportToNetSuite: () => `${prefix}/invoices/exporttonetsuite`,
        clearExport: () => `${prefix}/invoices/clearexport`,
        downloadInvoices: () => `${prefix}/invoices/downloadinvoices`,
        getExportFile: (id, type) => `${prefix}/invoices/getexportfile/${id}/${type}`,
        getCMExportFiles: () => `${prefix}/invoices/getcreditmemofiles`,
        invoiceExportsToClear: (id) => `${prefix}/invoices/invoiceexportstoclear/${id}`,
        excelExport: (rentalInvoices = false) => `${prefix}/invoices/excelExport/${rentalInvoices}`,
        slideoutStaff: (dispLocationId) => `${prefix}/invoice/slideoutStaff/${dispLocationId}`,
        slideoutEquipment: (dispLocationId) => `${prefix}/invoice/slideoutEquipment/${dispLocationId}`,
        syncInvoicesToNetSuite: () => `${prefix}/invoices/syncInvoicesToNetSuite`,
        syncInvoicesCMToNetSuite: () => `${prefix}/invoices/syncInvoicesCMToNetSuite`,
        getInvoiceNetSuiteReport: (id) => `${prefix}/invoices/getInvoiceNetSuiteReport/${id}`,
        printedInvoiceNumber: (id) => `${prefix}/invoice/printedInvoiceNumber/${id}`,
        createRentalInvoiceTask: (id) => `${prefix}/invoices/createRentalInvoiceTask/${id}`,
        includeDispatchOfficeName: (id) => `${prefix}/invoice/includeDispatchOfficeName/${id}`,
        getInvoiceChargesReport: (rentalInvoices) => `${prefix}/invoices/getInvoiceChargesReport/${rentalInvoices}`,
    },
    invoiceGeneration: {
        search: () => `${prefix}/invoiceGenerations/search`,
        excelExport: () => `${prefix}/invoiceGenerations/excelExport`,
        export: () => `${prefix}/invoiceGenerations/export`,
        reject: () => `${prefix}/invoiceGenerations/reject`,
        billingReject: () => `${prefix}/invoiceGenerations/billingReject`,
        adminReject: () => `${prefix}/invoiceGenerations/adminReject`,
        needsAdminReject: (id) => `${prefix}/invoiceGenerations/needsAdminReject/${id}`,
        download: (id) => `${prefix}/invoiceGeneration/download/${id}`,
        setEmergency: () => `${prefix}/invoiceGeneration/setEmergency`,
        setOnHold: () => `${prefix}/invoiceGeneration/setOnHold`,
        removeOnHold: () => `${prefix}/invoiceGeneration/removeOnHold`,
        generateChargeReport: () => `${prefix}/invoiceGenerations/generateChargeReport`,
    },
    localization: {
        strings: () => `${prefix}/localization/strings`,
        timezones: () => `${prefix}/localization/timezones`,
    },
    language: {
        all: () => `${prefix}/language`,
        tenant: () => `${prefix}/language/tenant`,
        me: () => `${prefix}/language/me`,
        forEmployee: (employeeId) => `${prefix}/language/employee/${employeeId}`,
        tenantSmsTemplates: () => `${prefix}/language/tenant/sms-templates`,
        tenantSmsTemplateByKey: (templateKey) => `${prefix}/language/tenant/sms-template/${templateKey}`,
    },
    locationContact: {
        all: () => `${prefix}/locationContacts`,
        byId: (id) => `${prefix}/locationContact/${id}`,
        create: () => `${prefix}/locationContact`,
        update: (id) => `${prefix}/locationContact/${id}`,
        search: () => `${prefix}/locationContacts/search`,
        forJob: (jobId) => `${prefix}/locationContacts/forJob/${jobId}`,
        excelExport: () => `${prefix}/locationContacts/excelExport`,
        toggleCustomerPortal: (id) => `${prefix}/locationContact/${id}/toggleUser`,
        getPasswordLink: (id) => `${prefix}/locationContact/${id}/passwordLink`,
        matchingContacts: (id) => `${prefix}/locationContacts/matching/${id}`,
    },
    longTermStatus: {
        all: () => `${prefix}/longTermStatuses`,
        byTypeId: (id) => `${prefix}/longTermStatuses/getByType/${id}`,
        byId: (id) => `${prefix}/longTermStatus/${id}`,
        create: () => `${prefix}/longTermStatus/create`,
        update: () => `${prefix}/longTermStatus/update`,
        search: () => `${prefix}/longTermStatuses/search`,
        delete: (id) => `${prefix}/longTermStatus/${id}`,
        getTypes: () => `${prefix}/typeahead/getLongTermStatusTypes`,
    },
    userGroup: {
        all: () => `${prefix}/userGroups`,
        search: () => `${prefix}/userGroups/search`,
        byId: (userGroupId) => `${prefix}/userGroup/${userGroupId}`,
        create: () => `${prefix}/userGroup`,
        update: (userGroupId) => `${prefix}/userGroup/${userGroupId}`,
        delete: (userGroupId) => `${prefix}/userGroup/${userGroupId}`,
    },
    permission: {
        all: () => `${prefix}/permissions`,
        search: () => `${prefix}/permissions/search`,
        byId: (permissionId) => `${prefix}/permission/${permissionId}`,
        create: () => `${prefix}/permission`,
        update: (permissionId) => `${prefix}/permission/${permissionId}`,
        delete: (permissionId) => `${prefix}/permission/${permissionId}`,
    },
    project: {
        search: () => `${prefix}/projects/search`,
        all: () => `${prefix}/projects`,
        byId: (projectId) => `${prefix}/project/${projectId}`,
        create: () => `${prefix}/project`,
        update: (projectId) => `${prefix}/project/${projectId}`,
        delete: (projectId) => `${prefix}/project/${projectId}`,
        closeReopenProject: (projectId, status) => `${prefix}/project/closeReopenProject/${projectId}/${status}`,
        createQuickProject: () => `${prefix}/project/createQuickProject`,
        bulkCreate: () => `${prefix}/project/bulk`,
        isWOExists: (contractId, workorderNumber, projectId) => `${prefix}/project/isWOExists/${contractId}/${workorderNumber}/${projectId}`,
        calculateRentalUnbilledRevenue: () => `${prefix}/projects/calculateRentalUnbilledRevenue`,
        excelExport: () => `${prefix}/projects/excelExport`,
        invoice: (projectId) => `${prefix}/project/invoice/${projectId}`,
        invoices: (projectId) => `${prefix}/project/${projectId}/invoices`,
        getLastSubmittedTimesheet: (projectId) => `${prefix}/project/getLastSubmittedTimesheet/${projectId}`,
        progress: (projectId) => `${prefix}/project/progress/${projectId}`,
        closureValidation: (projectId) => `${prefix}/project/closureValidation/${projectId}`,
        syncEstimate: (contractId) => `${prefix}/project/syncEstimate/${contractId}`,
    },
    job: {
        weeks: () => `${prefix}/jobs/weeks`,
        search: () => `${prefix}/jobs/search`,
        dailyView: () => `${prefix}/jobs/dailyView`,
        dailyViewHistory: () => `${prefix}/jobs/dailyViewHistory`,
        dailyViewExcelExport: () => `${prefix}/jobs/dailyViewExcelExport`,
        dailyViewHistoryExcelExport: () => `${prefix}/jobs/dailyViewHistoryExcelExport`,
        equipmentForJob: (requestedDate, jobId) => `${prefix}/jobs/equipment/${requestedDate}/${jobId}`,
        byId: (jobId) => `${prefix}/job/${jobId}`,
        byContractId: (contractId) => `${prefix}/job/${contractId}`,
        getResources: (contractId) => `${prefix}/job/getJobResources/${contractId}`,
        getNonResources: (contractId) => `${prefix}/job/getJobNonResources/${contractId}`,
        create: () => `${prefix}/job`,
        update: (jobId) => `${prefix}/job/${jobId}`,
        bulkUpdate: () => `${prefix}/job/bulkUpdate`,
        bulkMove: () => `${prefix}/job/bulkMove`,
        delete: (jobId) => `${prefix}/job/${jobId}`,
        postAttachments: (jobIdOrSessionId) => `${prefix}/job/attach/${jobIdOrSessionId}`,
        attachment: (jobIdOrSessionId) => `${prefix}/job/attachment/${jobIdOrSessionId}`,
        getAllAttachments: (jobId) => `${prefix}/job/${jobId}/attachments`,
        getTimesheetStatus: (jobId) => `${prefix}/timesheetStatus/${jobId}`,
        getDuplicateJobs: () => `${prefix}/job/getDuplicateJobs`,
        confirmedAssignments: () => `${prefix}/job/confirmedAssignments`,
        rate: () => `${prefix}/job/rate`,
    },
    jobAssignment: {
        byId: (assignmentId) => `${prefix}/jobAssignment/${assignmentId}`,
        confirm: (assignmentId) => `${prefix}/jobAssignment/confirm/${assignmentId}`,
        callOff: (assignmentId) => `${prefix}/jobAssignment/callOff/${assignmentId}`,
        callOffDisclaimer: () => `${prefix}/jobAssignment/callOffDisclaimer`,
        notify: () => `${prefix}/jobAssignments/notify`,
        notifyJobUpdate: () => `${prefix}/jobAssignments/notifyJobUpdate`,
        geofences: () => `${prefix}/jobAssignments/geofences`,
        setEmergency: () => `${prefix}/jobAssignments/setEmergency`,
        eventLog: () => `${prefix}/jobAssignments/eventLog`,
        getEmployeeActiveAssignments: (employeeId) => `${prefix}/jobAssignment/getEmployeeActiveAssignments/${employeeId}`,
    },
    messages: {
        assignmentDeliveryStatus: () => `${prefix}/messages/assignmentDeliveryStatus`,
        reOptIn: (messageRecipientId) => `${prefix}/messages/${messageRecipientId}/reopt-in`,
        resend: (messageRecipientId) => `${prefix}/messages/${messageRecipientId}/resend`,
    },
    jobAssignmentCancellation: {
        byId: (assignmentCancellationId) => `${prefix}/jobAssignmentCancellation/${assignmentCancellationId}`,
        cancelConfirmed: (assignmentCancellationId) => `${prefix}/jobAssignmentCancellation/cancelConfirmed/${assignmentCancellationId}`,
    },
    jobAssignmentCancellationType: {
        byId: (id) => `${prefix}/jobAssignmentCancellationType/${id}`,
        all: () => `${prefix}/jobAssignmentCancellationTypes/all`,
        search: () => `${prefix}/jobAssignmentCancellationTypes/search`,
        create: () => `${prefix}/jobAssignmentCancellationType/create`,
        update: () => `${prefix}/jobAssignmentCancellationType/update`,
        delete: (id) => `${prefix}/jobAssignmentCancellationType/${id}`,
    },
    jobType: {
        search: () => `${prefix}/jobTypes/search`,
        update: () => `${prefix}/jobType/update`,
    },
    locationContactType: {
        all: () => `${prefix}/locationContactTypes`,
    },
    locationType: {
        search: () => `${prefix}/locationTypes/search`,
        byId: (locationTypeId) => `${prefix}/locationType/${locationTypeId}`,
        create: () => `${prefix}/locationType`,
        update: (locationTypeId) => `${prefix}/locationType/${locationTypeId}`,
        delete: (locationTypeId) => `${prefix}/locationType/${locationTypeId}`,
    },
    paySchedule: {
        search: () => `${prefix}/payrollManagements/searchPaySchedules`,
        all: () => `${prefix}/payrollManagements/paySchedules`,
        byId: (payScheduleId) => `${prefix}/payrollManagements/paySchedule/${payScheduleId}`,
        update: (payScheduleId) => `${prefix}/payrollManagements/paySchedule/${payScheduleId}`,
    },
    payroll: {
        search: () => `${prefix}/payrolls/search`,
        searchindividual: () => `${prefix}/payrolls/searchindividual`,
        searchfullview: () => `${prefix}/payrolls/searchfullview`,
        excelExport: () => `${prefix}/payrolls/excelExport`,
        excelExportIndividual: () => `${prefix}/payrolls/excelExportIndividual`,
        approve: () => `${prefix}/payrolls/approve`,
        payrollReject: () => `${prefix}/payrolls/payrollReject`,
        fringeReport: () => `${prefix}/payrolls/fringeReport`,
        certifiedPayroll: () => `${prefix}/payrolls/certifiedPayroll`,
        excelWeeklyExport: () => `${prefix}/payrolls/excelWeeklyExport`,
        excelHoursExport: () => `${prefix}/payrolls/excelHoursExport`,
        employees: () => `${prefix}/payrolls/employees`,
        unionExport: () => `${prefix}/payrolls/unionExport`,
        download: (id) => `${prefix}/payroll/download/${id}`,
        downloadWeekly: (id) => `${prefix}/payroll/downloadWeekly/${id}`,
        edit: () => `${prefix}/payroll/edit`,
        doesPayrollHaveEdits: () => `${prefix}/payroll/doesPayrollHaveEdits`,
        doesPayrollHaveExports: () => `${prefix}/payroll/doesPayrollHaveExports`,
        overridePayRate: (payrollId, payRate) => `${prefix}/payroll/overridePayRate/${payrollId}/${payRate}`,
        updateWorkOrderNumber: (id) => `${prefix}/payroll/${id}/workOrderNumber`,
    },
    payrollManagement: {
        search: () => `${prefix}/payrollManagements/search`,
        excelExport: () => `${prefix}/payrollManagements/excelExport`,
        slideout: (payrollId) => `${prefix}/payrollManagement/${payrollId}/slideout`,
        add: () => `${prefix}/payrollManagement/add`,
        edit: () => `${prefix}/payrollManagement/edit`,
        approve: () => `${prefix}/payrollManagements/approve`,
        process: () => `${prefix}/payrollManagements/process`,
        adminReject: () => `${prefix}/payrollManagements/adminReject`,
        needsAdminReject: (id) => `${prefix}/payrollManagements/needsAdminReject/${id}`,
        reject: () => `${prefix}/payrollManagements/reject`,
        download: (id) => `${prefix}/payrollManagement/download/${id}`,
        downloadWeekly: (id) => `${prefix}/payrollManagement/downloadWeekly/${id}`,
        setEmergency: () => `${prefix}/payrollManagement/setEmergency`,
        recalculateWeek: () => `${prefix}/payrollManagement/recalculateWeek`,
        payrollChargeLocations: () => `${prefix}/payrollManagement/payrollChargeLocations`,
        payrollCharges: (locationId) => `${prefix}/payrollManagement/payrollCharges/${locationId}`,
        addPayrollCharge: () => `${prefix}/payrollManagement/addPayrollCharge`,
    },
    quickbooks: {
        search: () => `${prefix}/quickBooksInventoryItems/search`,
        create: () => `${prefix}/quickBooksInventoryItem/new`,
        update: (qbiId) => `${prefix}/quickBooksInventoryItem/${qbiId}`,
        edit: (qbiId) => `${prefix}/quickBooksInventoryItem/${qbiId}`,
        excelExport: () => `${prefix}/quickBooksInventoryItems/excelExport`,
    },
    report: {
        timesheet: (timesheetId) => `${prefix}/report/timesheet/${timesheetId}`,
        timesheetSetup: (timesheetId) => `${prefix}/report/timesheet/${timesheetId}/setup`,
        timesheetBilling: (timesheetId) => `${prefix}/report/timesheet/${timesheetId}/billing`,
        timesheetPayroll: (timesheetId) => `${prefix}/report/timesheet/${timesheetId}/payroll`,
        timesheetPayrollIndividual: (timesheetId) => `${prefix}/report/timesheet/${timesheetId}/payrollIndividual`,
        invoice: (invoiceId) => `${prefix}/report/invoice/${invoiceId}`,
        newCustomerBillableHours: () => `${prefix}/report/newCustomerBillableHours`,
        staffing: () => `${prefix}/report/staffing`,
        expiringContract: () => `${prefix}/report/expiringContract`,
        accrualReport: () => `${prefix}/report/getAccrualReport`,
        downloadDailyOperationalReport: (tenantId, organizationId) => {
            let url = `${prefix}/report/downloadDailyOperationalReport?tenantId=${tenantId}`;

            if (organizationId) {
                url += `&organizationId=${encodeURIComponent(organizationId)}`;
            }

            return url;
        },
        downloadDailyOperationalReportExcel: (tenantId, organizationId) => {
            let url = `${prefix}/report/downloadDailyOperationalReportExcel?tenantId=${tenantId}`;

            if (organizationId) {
                url += `&organizationId=${encodeURIComponent(organizationId)}`;
            }

            return url;
        },
    },
    resourceKit: {
        search: () => `${prefix}/resourceKits/search`,
        byId: (id) => `${prefix}/resourceKit/${id}`,
        create: () => `${prefix}/resourceKit`,
        update: (id) => `${prefix}/resourceKit/${id}`,
        delete: (id) => `${prefix}/resourceKit/${id}`,
        byTenant: () => `${prefix}/resourceKits/byTenant`,
    },
    role: {
        all: () => `${prefix}/roles`,
        search: () => `${prefix}/roles/search`,
        byId: (roleId) => `${prefix}/role/${roleId}`,
        create: () => `${prefix}/role`,
        update: (roleId) => `${prefix}/role/${roleId}`,
        delete: (roleId) => `${prefix}/role/${roleId}`,
    },
    route: {
        lookup: () => `${prefix}/route/lookup`,
    },
    selfDispatchTimesheet: {
        byId: (id) => `${prefix}/selfDispatchTimesheet/${id}`,
        softDelete: () => `${prefix}/selfDispatchTimesheet/softDelete`,
        equipmentByChargeType: (chargeTypeId) => `${prefix}/selfDispatchTimesheet/equipment/${chargeTypeId}`,
        create: () => `${prefix}/selfDispatchTimesheet`,
        selfDispatchNotifications: () => `${prefix}/selfDispatchTimesheet/selfDispatchNotifications`,
        nonFlagNotifications: () => `${prefix}/selfDispatchTimesheet/nonFlagNotifications`,
        count: () => `${prefix}/selfDispatchTimesheet/count`,
    },
    tenant: {
        all: () => `${prefix}/tenants/all`,
        byUserRole: () => `${prefix}/tenants/byUserRole`,
        myAdminTenantIds: () => `${prefix}/tenants/myAdminTenantIds`,
        byId: (companyId) => `${prefix}/tenant/${companyId}`,
        themeById: (companyId) => `${prefix}/tenant/tenantTheme/${companyId}`,
        create: () => `${prefix}/tenant/create`,
        onboardingStatus: (companyId) => `${prefix}/tenant/onboardingStatus/${companyId}`,
        search: () => `${prefix}/tenants/search`,
        settings: () => `${prefix}/tenant/settings`,
        settingsById: (companyId) => `${prefix}/tenant/settings/${companyId}`,
        viewModel: () => `${prefix}/tenants/viewModel`,
        invalidPhoneNumbers: () => `${prefix}/tenant/invalidphonenumbers`,
        optionalCompanyTypes: () => `${prefix}/tenant/optionalCompanyTypes`,
        optionalContractScheduleTypes: () => `${prefix}/tenant/optionalContractScheduleTypes`,
        optionalJobTypes: () => `${prefix}/tenant/optionaljobtypes`,
        countyCaptureTypes: () => `${prefix}/tenant/countyCaptureTypes`,
        getLogo: () => `${prefix}/tenant/logo`,
        getTenantLogo: (tenantId) => `${prefix}/tenant/${tenantId ?? 1}/logo`,
        updateBrandLogo: (tenantId, useAsAppLogo) => `${prefix}/tenant/${tenantId}/updateBrandLogo/${useAsAppLogo}`,
        updateReportLogo: (tenantId) => `${prefix}/tenant/${tenantId}/updateReportLogo`,
        getReportLogo: (tenantId) => `${prefix}/tenant/${tenantId}/reportLogo`,
        getThemeById: (companyId) => `${prefix}/tenant/theme/${companyId}`,
        updateDispatchTimezones: (tenantId) => `${prefix}/tenant/${tenantId}/updateDispatchTimezones`,
        updateEmployeesTimezones: (tenantId) => `${prefix}/tenant/${tenantId}/updateEmployeesTimezones`,
    },

    timesheet: {
        overview: (timesheetId) => `${prefix}/timesheet/${timesheetId}/overview`,
        slideout: (timesheetId, payroll, billing) => `${prefix}/timesheet/${timesheetId}/slideout?payroll=${payroll}&billing=${billing}`,
        slideoutStaff: (dispLocationId) => `${prefix}/timesheet/slideoutStaff/${dispLocationId}`,
        slideoutEquipment: (dispLocationId) => `${prefix}/timesheet/slideoutEquipment/${dispLocationId}`,
        projectPickupEquipment: (projectId) => `${prefix}/timesheet/projectPickupEquipment/${projectId}`,
        byId: (timesheetId) => `${prefix}/timesheet/${timesheetId}`,
        byEmp: (timesheetId, empId) => `${prefix}/timesheet/${timesheetId}?empId=${empId}`,
        crewDetails: (timesheetId, employeeId) => `${prefix}/timesheet/${timesheetId}/${employeeId}`,
        update: () => `${prefix}/timesheet/updateTimesheet`,
        createLinkedTimesheet: (parentId) => `${prefix}/timesheet/createLinkedTimesheet/${parentId}`,
        updateWorkOrderNumber: (id) => `${prefix}/timesheet/${id}/workOrderNumber`,
        updateRentalDate: (timesheetDetailId) => `${prefix}/timesheet/detail/${timesheetDetailId}/rentalDate`,
        undoClientCancel: () => `${prefix}/timesheet/undoClientCancel`,
        updateForeman: (id) => `${prefix}/timesheet/${id}/foreman`,
        updateRequestor: (id) => `${prefix}/timesheet/${id}/requestor`,
        setEmergency: () => `${prefix}/timesheet/setEmergency`,
        byEmployeeSearch: () => `${prefix}/timesheets/searchByEmployee`,
        recentByEmployee: (employeeId) => `${prefix}/timesheets/recentByEmployee/${employeeId}`,
        getTimesheetCharges: (timesheetId) => `${prefix}/timesheet/getTimesheetCharges/${timesheetId}`,
        audit: () => `${prefix}/timesheet/audit`,
        equipmentAudit: (timesheetId) => `${prefix}/timesheet/${timesheetId}/equipment/audit`,
        estDriveTime: (timesheetId, employeeId) => `${prefix}/timesheet/${timesheetId}/driveTime/${employeeId}`,
        getSameTypeCharges: (contractId, chargeTypeId) => `${prefix}/timesheet/getContractCharges/${contractId}/${chargeTypeId}`,
        getSameTypeCharges_timesheet: (timesheetId, chargeTypeId) => `${prefix}/timesheet/getTimesheetCharges/${timesheetId}/${chargeTypeId}`,
        rollBackApproved: (timesheetId) => `${prefix}/timesheet/${timesheetId}/rollBackApproved`,
        updateAttachments: (timesheetId, scope = TimesheetAttachmentScope.All) => `${prefix}/timesheet/${timesheetId}/attachments?scope=${scope}`,
        getAttachments: (timesheetId, scope = TimesheetAttachmentScope.All) => `${prefix}/timesheet/${timesheetId}/attachments?scope=${scope}`,
        deleteAttachment: (timesheetId, attachmentId, scope = TimesheetAttachmentScope.All) =>
            `${prefix}/timesheet/${timesheetId}/attachments/${attachmentId}?scope=${scope}`,
        createPickupTimesheet: () => `${prefix}/timesheet/createPickupTimesheet`,
    },
    timesheetReview: {
        byId: (reviewId) => `${prefix}/timesheetReview/${reviewId}`,
        update: () => `${prefix}/timesheetReview/updateTimesheetReview`,
        clientByTimesheetId: (timesheetId) => `${prefix}/timesheetReview/client/${timesheetId}`,
        employeesByTimesheetId: (timesheetId) => `${prefix}/timesheetReview/employee/${timesheetId}`,
        missingSignatureReasons: () => `${prefix}/timesheetReview/missingSignatureReasons`,
        resetClientSignature: (timesheetId) => `${prefix}/timesheetReview/resetClientSignature/${timesheetId}`,
        postWorkCard: (timesheetId, selectedEmployeeId) => `${prefix}/timesheetReview/uploadEmployeeWorkCards/${timesheetId}/${selectedEmployeeId}`,
        getEmployeeWorkCard: (timesheetId, employeeId) => `${prefix}/timesheetReview/getEmployeeWorkCard/${timesheetId}/${employeeId}`,
        signatureImage: (reviewId) => `${prefix}/timesheetReview/${reviewId}/signatureImage`,
    },
    timesheetManagement: {
        search: () => `${prefix}/timesheetManagements/search`,
        searchCounts: () => `${prefix}/timesheetManagements/searchCounts`,
        export: () => `${prefix}/timesheetManagements/export`,
        download: (id) => `${prefix}/timesheetManagement/download/${id}`,
        excelExport: () => `${prefix}/timesheetManagements/excelExport`,
        accept: (timesheetId) => `${prefix}/timesheetManagement/accept/${timesheetId}`,
        modify: (timesheetId) => `${prefix}/timesheetManagement/modify/${timesheetId}`,
        reject: () => `${prefix}/timesheetManagement/reject`,
        flags: (timesheetId) => `${prefix}/timesheetManagement/flags/${timesheetId}`,
        dismissFlag: () => `${prefix}/timesheetManagement/dismissFlag`,
        softDelete: () => `${prefix}/timesheetManagement/softDelete`,
        timesheetsBelongingToJobs: (ids) => `${prefix}/timesheetManagement/timesheetsBelongingToJobs/${ids}`,
        restoreTimesheet: (timesheetId) => `${prefix}/timesheetManagement/restoreTimesheet/${timesheetId}`,
        timesheetStatusHistoryReport: () => `${prefix}/timesheetManagements/timesheetStatusHistoryReport`,
    },
    countries: {
        byCountryId: (id) => `${prefix}/country/${id}`,
    },
    USStates: {
        all: () => `${prefix}/usstates`,
        search: () => `${prefix}/usstates/search`,
        update: (id) => `${prefix}/usstates/${id}`,
        getById: (id) => `${prefix}/usstates/${id}`,
    },
    USCounties: {
        all: () => `${prefix}/usCounties`,
        groups: () => `${prefix}/usCounties/groups`,
        groupTypes: () => `${prefix}/usCounties/groupTypes`,
        groupPaySources: () => `${prefix}/usCounties/groupPaySources`,
        byGroupId: (id) => `${prefix}/usCounties/group/${id}`,
        group: () => `${prefix}/usCounties/group`,
        addTimeZone: (id) => `${prefix}/usCounties/addTimeZone/${id}`,
        pWCountyGroups: () => `${prefix}/usCounties/pWCountyGroups`,
    },
    jobRequest: {
        jobRequest: () => `${prefix}/jobRequest`,
        byId: (id) => `${prefix}/jobRequest/${id}`,
        updateStatus: (requestId, statusId) => `${prefix}/jobRequest/${requestId}/status/${statusId}`,
        reject: (requestId) => `${prefix}/jobRequest/${requestId}/reject`,
        parseXlsx: () => `${prefix}/jobRequest/parse-xlsx`,
        bulkCreate: () => `${prefix}/jobRequest/bulk-create`,
    },
    jobRequests: {
        getStatuses: () => `${prefix}/jobRequests/getStatuses`,
        getCustomers: () => `${prefix}/jobRequests/getCustomers`,
        currentForeman: (customerCompanyId) => `${prefix}/jobRequests/currentForeman/${customerCompanyId}`,
        search: () => `${prefix}/jobRequests/search`,
        forUser: (requestedDate, jobId) => `${prefix}/jobRequests/forUser/${requestedDate}/${jobId || ''}`,
        equipmentForTimesheet: (requestedDate, timesheetId) => `${prefix}/jobRequests/equipment/${requestedDate}/${timesheetId}`,
        crewEta: (timesheetId) => `${prefix}/jobRequests/${timesheetId}/crew-eta`,
        updateJobLocation: (jobId) => `${prefix}/jobRequests/jobLocation/${jobId}`,
        updateJobRequestLocation: (jobRequestId) => `${prefix}/jobRequests/jobRequestLocation/${jobRequestId}`,
    },
    typeAheads: {
        tenants: () => `${prefix}/typeahead/getTenants`,
        foremansByCompany: (companyId) => `${prefix}/typeahead/foremansByCompany/${companyId}`,
        jobCities: (partialTextSearch) => `${prefix}/typeahead/getJobCities/${partialTextSearch}`,
        companies: () => `${prefix}/typeahead/getCompanies`,
        customers: () => `${prefix}/typeahead/getCustomers`,
        customersByDispatchLocation: (dispatchLocationId) => `${prefix}/typeahead/getCustomersByDispatchLocation/${dispatchLocationId}`,
        accessibleCustomers: () => `${prefix}/typeahead/getAccessibleCustomers`,
        contractsForCustomer: (customerCompanyId, dispatchLocationId) => `${prefix}/typeahead/getContractsForCustomer/${customerCompanyId}/${dispatchLocationId}`,
        quickRentalContractsForCustomer: (customerCompanyId, dispatchLocationId) => `${prefix}/typeahead/getQuickRentalContractsForCustomer/${customerCompanyId}/${dispatchLocationId}`,
        quickRentalContractTemplates: () => `${prefix}/typeahead/getQuickRentalContractTemplates`,
        callLogInteractionTypes: () => `${prefix}/typeahead/getCallLogInteractionTypes`,
        callDirections: () => `${prefix}/typeahead/getCallDirections`,
        contactCategories: () => `${prefix}/typeahead/getContactCategories`,
        callDispositions: () => `${prefix}/typeahead/getCallDispositions`,
        parentCustomers: () => `${prefix}/typeahead/getParentCustomers`,
        subcontractingCustomers: () => `${prefix}/typeahead/getSubcontractingCustomers`,
        billingCompanies: () => `${prefix}/typeahead/getBillingCompanies`,
        vendorBillOffices: () => `${prefix}/typeahead/getVendorBillOffices`,
        contracts: () => `${prefix}/typeahead/getContracts`,
        getProjectInvoiceDate: (timesheetId) => `${prefix}/typeahead/getProjectInvoiceDate/${timesheetId}`,
        contractsSearch: (partialTextSearch, projectOnly) => `${prefix}/typeahead/getContractNumber/${partialTextSearch}?projectOnly=${projectOnly}`,
        contractsSearchByTenant: (tenantId, partialTextSearch) => `${prefix}/typeahead/getContractNumberByTenant/${tenantId}/${partialTextSearch}`,
        contractsByCompany: (companyId) => `${prefix}/typeahead/getContractsByCompany/${companyId}`,
        flaggingContractTemplates: () => `${prefix}/typeahead/getFlaggingContractTemplates`,
        companyChargeTypeGroups: (companyId) => `${prefix}/typeahead/getCompanyChargeTypeGroups/${companyId}`,
        billingChargeTypes: (timesheetId) => `${prefix}/typeahead/getBillingChargeTypes/${timesheetId}`,
        billingContacts: (companyId) => `${prefix}/typeahead/getBillingContacts/${companyId}`,
        dispatchLocations: () => `${prefix}/typeahead/getDispatchLocations`,
        allDispatchLocations: () => `${prefix}/typeahead/getAllDispatchLocations`,
        selectableDispatchLocations: (selectedOfficeId) =>
            `${prefix}/typeahead/getSelectableDispatchLocations${selectedOfficeId ? `/${selectedOfficeId}` : ''}`,
        dispatchCompaniesForDelegate: (employeeId) =>
            `${prefix}/typeahead/getDispatchCompaniesForDelegate${employeeId ? `/${employeeId}` : ''}`,
        contractDispatchLocations: (contractId) => `${prefix}/typeahead/getContractDispatchLocations/${contractId}`,
        dispatchCompaniesForContract: (contractId) =>
            `${prefix}/typeahead/getDispatchCompaniesForContract${contractId ? `/${contractId}` : ''}`,
        dispatchCompaniesForJob: (jobId) => `${prefix}/typeahead/getDispatchCompaniesForJob${jobId ? `/${jobId}` : ''}`,
        dispatchCompaniesForProject: (projectId) =>
            `${prefix}/typeahead/getDispatchCompaniesForProject${projectId ? `/${projectId}` : ''}`,
        dispatchCompaniesForQuickSale: (contractId, quickSaleId) =>
            `${prefix}/typeahead/getDispatchCompaniesForQuickSale/${contractId}${quickSaleId ? `/${quickSaleId}` : ''}`,
        dispatchCompaniesForEmployee: (employeeId, parentCompanyId) =>
            `${prefix}/typeahead/getDispatchCompaniesForEmployee${employeeId ? `/${employeeId}` : ''}${parentCompanyId ? `?parentCompanyId=${parentCompanyId}` : ''
            }`,
        dispatchCompaniesForEquipment: (equipmentId) =>
            `${prefix}/typeahead/getDispatchCompaniesForEquipment${equipmentId ? `/${equipmentId}` : ''}`,
        dispatchCompaniesForDispatchPlus: (dispatchCompanyId) =>
            `${prefix}/typeahead/getDispatchCompaniesForDispatchPlus${dispatchCompanyId ? `/${dispatchCompanyId}` : ''}`,
        payrollCompanies: () => `${prefix}/typeahead/getPayrollCompanies`,
        companyTypes: () => `${prefix}/typeahead/getCompanyTypes`,
        companyStatuses: () => `${prefix}/typeahead/getCompanyStatus`,
        contractStatuses: () => `${prefix}/typeahead/getContractStatus`,
        chargeTypeUnits: () => `${prefix}/typeahead/getChargeTypeUnits`,
        chargeTypes: () => `${prefix}/typeahead/getChargeTypes`,
        surchargeChargeTypes: () => `${prefix}/typeahead/getSurcharges`,
        chargeTypeSearch: (partialTextSearch) => `${prefix}/typeahead/getChargeTypes/${partialTextSearch}`,
        chargeTypeSearchByTenant: (partialTextSearch, tenantId) => `${prefix}/typeahead/getChargeTypesByTenantId/${partialTextSearch}/${tenantId}`,
        chargeTypesBillable: (includeNetSuite) => `${prefix}/typeahead/getChargeTypesBillable/${includeNetSuite ?? false}`,
        chargeTypeApplicability: () => `${prefix}/typeahead/getChargeTypeApplicability`,
        chargeTypeTrackingTypes: () => `${prefix}/typeahead/getChargeTypeTrackingTypes`,
        contractScheduleTypes: () => `${prefix}/typeahead/getContractScheduleTypes`,
        getChargeTypePaySources: () => `${prefix}/typeahead/getChargeTypePaySources`,
        contractUnionWorkTypes: () => `${prefix}/typeahead/getUnionWorkTypes`,
        contractUnionWorkTypesUsedByTenant: () => `${prefix}/typeahead/getUnionWorkTypesUsedByTenant`,
        employeeGroups: () => `${prefix}/typeahead/getEmployeeGroups`,
        employees: () => `${prefix}/typeahead/getEmployees`,
        searchEmployees: (searchText) => `${prefix}/typeahead/searchEmployees?searchText=${encodeURIComponent(searchText)}`,
        equipmentTypes: () => `${prefix}/typeahead/getEquipmentTypes`,
        equipmentTypeAFADTypes: () => `${prefix}/typeahead/getEquipmentTypeAFADTypes`,
        equipmentTypeStatuses: () => `${prefix}/typeahead/getEquipmentTypeStatuses`,
        locationTypes: () => `${prefix}/typeahead/getLocationTypes`,
        permissions: () => `${prefix}/typeahead/getPermissions`,
        pendingBillingTimesheets: () => `${prefix}/typeahead/getPendingBillingTimesheets`,
        pendingPayrollTimesheetsSearch: (partialTextSearch) => `${prefix}/typeahead/getPendingPayrollTimesheetsSearch/${partialTextSearch}`,
        pendingPayrollTimesheets: () => `${prefix}/typeahead/getPendingPayrollTimesheets`,
        roles: () => `${prefix}/typeahead/getRoles`,
        USStates: () => `${prefix}/typeahead/getUSStates`,
        USStatesbyCountryId: (countryId) => `${prefix}/typeahead/getUSStates/${countryId}`,
        USStatesbyCompanyId: (companyId) => `${prefix}/typeahead/getUSStatesbyCompany/${companyId}`,
        crewSignatureTypes: () => `${prefix}/typeahead/getCrewSignatureTypes`,
        clientSignatureTypes: () => `${prefix}/typeahead/getClientSignatureTypes`,
        jobAssignmentCancellationTypes: () => `${prefix}/typeahead/getJobAssignmentCancellationTypes`,
        jobResourceTypes: () => `${prefix}/typeahead/getJobResourceTypes`,
        jobCancellationTypes: () => `${prefix}/typeahead/getJobCancellationTypes`,
        timesheetNumberSearch: (partialTextSearch) => `${prefix}/typeahead/getTimesheetNumber/${partialTextSearch}`,
        searchTimesheetNumber: (partialTextSearch) => `${prefix}/typeahead/searchTimesheetNumber/${partialTextSearch}`,
        timesheetStatuses: () => `${prefix}/typeahead/getTimesheetStatuses`,
        timesheetDetailStatuses: () => `${prefix}/typeahead/getTimesheetDetailStatuses`,
        timesheetDetails: (timesheetId) => `${prefix}/typeahead/getTimesheetDetails/${timesheetId}`,
        payrollExports: (partialTextSearch) => `${prefix}/typeahead/getPayrollExports/${partialTextSearch}`,
        payrollExportOffices: (payrollExportId) => `${prefix}/typeahead/getPayrollExportOffices/${payrollExportId}`,
        payrollExportEmployees: (payrollExportId, officeIds) => (
            `${prefix}/typeahead/getPayrollExportEmployees/${payrollExportId}?${(officeIds ?? [])
                .map((id) => `officeIds=${id}`)
                .join('&')}`
        ),
        payrollStatuses: () => `${prefix}/typeahead/getPayrollStatuses`,
        timesheetManagementStatuses: () => `${prefix}/typeahead/getTimesheetManagementStatuses`,
        timesheetBillingStatuses: () => `${prefix}/typeahead/getTimesheetBillingStatuses`,
        billingStatuses: () => `${prefix}/typeahead/getBillingStatuses`,
        billingChargeStatuses: () => `${prefix}/typeahead/getBillingChargeStatuses`,
        operationsCenters: () => `${prefix}/typeahead/getOperationsCenters`,
        subcontractors: (customerId) => `${prefix}/typeahead/getSubcontractors/${customerId}`,
        subcontractorsByContract: (contractId) => `${prefix}/typeahead/getSubcontractorsByContract/${contractId}`,
        billingChargeFlags: () => `${prefix}/typeahead/getBillingChargeFlags`,
        billingCategoryFlags: () => `${prefix}/typeahead/getBillingCategoryFlags`,
        payrollChargeFlags: () => `${prefix}/typeahead/getPayrollChargeFlags`,
        payrollCategoryFlags: () => `${prefix}/typeahead/getPayrollCategoryFlags`,
        payrollChargeTypes: (timesheetId) => `${prefix}/typeahead/getPayrollChargeTypes/${timesheetId}`,
        invoiceTerms: () => `${prefix}/typeahead/getInvoiceTerms`,
        invoiceMapping: () => `${prefix}/typeahead/getInvoiceMapping`,
        invoiceStatuses: () => `${prefix}/typeahead/getInvoiceStatuses`,
        activityTaskStatuses: () => `${prefix}/typeahead/getActivityTaskStatuses`,
        activityTaskTypes: () => `${prefix}/typeahead/getActivityTaskTypes`,
        taskInteractionTypes: () => `${prefix}/typeahead/getTaskInteractionTypes`,
        chargeTypeGroups: () => `${prefix}/typeahead/getChargeTypeGroups`,
        quickBooksSalesAccounts: () => `${prefix}/typeahead/getQuickBooksInventoryItemSalesAccounts`,
        quickBooksInventoryItems: () => `${prefix}/typeahead/getQuickBooksInventoryItems`,
        payPeriodTypes: () => `${prefix}/typeahead/getPayPeriodTypes`,
        timesheetEmergencyPayTypes: () => `${prefix}/typeahead/getTimesheetEmergencyPayTypes`,
        rentalUnitTypes: () => `${prefix}/typeahead/getRentalUnitTypes`,
        serviceItems: () => `${prefix}/typeahead/getServiceItems`,
        countries: () => `${prefix}/typeahead/getCountries`,
        subcontractorSearch: (partialTextSearch) => `${prefix}/typeahead/getSubcontractor/${partialTextSearch}`,
        jotFormTypes: () => `${prefix}/typeahead/getJotFormTypes`,
        flagTypes: () => `${prefix}/typeahead/getFlagTypes`,
        rejectionReasons: () => `${prefix}/typeahead/getRejectionReasons`,
        billingCycles: () => `${prefix}/typeahead/getBillingCycles`,
        jobTypes: () => `${prefix}/typeahead/getJobTypes`,
        allEmployees: (tenantId, partialTextSearch) => `${prefix}/typeahead/getAllEmployees/${tenantId}/${partialTextSearch}`,
        getCancelReasonsByCancellationTypeId: (jobCancellationTypeId) => `${prefix}/typeahead/getCancelReasonsByCancellationTypeId/${jobCancellationTypeId}`,
        rentalProjects: () => `${prefix}/typeahead/rentalProjects`,
        openProjectsByContract: (contractId, includeDeleted = false) => `${prefix}/typeahead/getOpenProjectByContract/${contractId}?includeDeleted=${includeDeleted}`,
        openRentalProjectsByContract: (contractId) => `${prefix}/typeahead/getOpenRentalProjectsByContract/${contractId}`,
        baseUrl: () => `${prefix}/typeahead/baseUrl`,
        getAllQuestion: () => `${prefix}/typeahead/getAllQuestion`,
        chargeTypeQuestion: (chargeTypeId) => `${prefix}/typeahead/getChargetypeQuestion/${chargeTypeId}`,
        operationalCategories: () => `${prefix}/typeahead/getOperationalCategories`,
        employeesByDispatchLocation: (dispatchLocationId) => `${prefix}/typeahead/employeesByDispatchLocation/${dispatchLocationId}`,
        newHires: () => `${prefix}/typeahead/newHires`,
        dispatchPorts: (dispatchOfficeId) => `${prefix}/typeahead/${dispatchOfficeId}/ports`,
        certifiedPayrollForms: () => `${prefix}/typeahead/certifiedPayrollForms`,
        chargeTypeGroupStatuses: () => `${prefix}/typeahead/getChargeTypeGroupStatus`,
        creditMemoTypeOptions: () => `${prefix}/typeahead/creditMemoTypeOptions`,
    },
    workOrders: {
        all: () => `${prefix}/workorders`,
    },
    jotForms: {
        search: () => `${prefix}/jotForms/search`,
        create: () => `${prefix}/jotForms/create`,
        update: (jotFormId) => `${prefix}/jotForms/${jotFormId}`,
        getById: (jotFormId) => `${prefix}/jotForms/${jotFormId}`,
        submit: (submissionId) => `${prefix}/jotForms/submit/${submissionId}`,
        newPreJobFormSubmission: (timesheetId) => `${prefix}/jotForms/preJobForm/${timesheetId}/new`,
        getPreJobFormSubmission: (timesheetId) => `${prefix}/jotForms/preJobForm/${timesheetId}`,
        editPreJobFormSubmission: (submissionId) => `${prefix}/jotForms/preJobForm/${submissionId}/edit`,
    },
    equipmentTags: {
        all: () => `${prefix}/equipmentTag/all`,
        search: () => `${prefix}/equipmentTag/search`,
        create: () => `${prefix}/equipmentTag/create`,
        update: (id) => `${prefix}/equipmentTag/${id}`,
        getById: (id) => `${prefix}/equipmentTag/${id}`,
    },
    operationalCategories: {
        all: () => `${prefix}/operationalCategory/all`,
        search: () => `${prefix}/operationalCategory/search`,
        create: () => `${prefix}/operationalCategory/create`,
        update: (id) => `${prefix}/operationalCategory/${id}`,
        getById: (id) => `${prefix}/operationalCategory/${id}`,
    },
    delegate: {
        create: () => `${prefix}/delegate`,
        search: () => `${prefix}/delegates/search`,
        byId: (delegateId) => `${prefix}/delegate/${delegateId}`,
        update: () => `${prefix}/delegate`,
        delete: (delegateId) => `${prefix}/delegate/${delegateId}`,
    },
    quickSearch: {
        search: () => `${prefix}/quicksearch`,
    },
    quickSale: {
        search: () => `${prefix}/quickSales/search`,
        companies: () => `${prefix}/quickSales/companies`,
        contracts: (companyId, quickSaleId) => `${prefix}/quickSales/contracts/${companyId}${quickSaleId ? `?quickSaleId=${quickSaleId}` : ''}`,
        contract: (contractId) => `${prefix}/quickSales/contract/${contractId}`,
        create: () => `${prefix}/quickSales`,
        update: (id) => `${prefix}/quickSales/${id}`,
        delete: (id) => `${prefix}/quickSales/${id}`,
        byId: (id) => `${prefix}/quickSales/${id}`,
    },
    customForm: {
        create: () => `${prefix}/customform`,
        delete: (id) => `${prefix}/customform/${id}`,
        update: (id) => `${prefix}/customform/${id}`,
        forContext: () => `${prefix}/customform/context`,
        forTimesheets: (timesheetIds, formContextId) => `${prefix}/customform/timesheets?${timesheetIds.map((id) => `timesheetIds=${id}`).join('&')}&formContextId=${formContextId}`,
        forQuickSale: (quickSaleId) => `${prefix}/customform/quickSale?${quickSaleId}`,
        processOcr: () => `${prefix}/customform/ocr`,
    },
    fileUpload: {
        createAttachment: () => `${prefix}/attachment`,
        deleteAttachment: () => `${prefix}/attachment`,
        getAttachmentById: (id) => `${prefix}/attachment/${id}`,
        streamAttachmentById: (id) => `${prefix}/attachment/stream/${id}`,
    },
    public: {
        getAttachmentById: (id) => `${prefix}/public/attachment/${id}`,
    },
    shortcut: {
        create: () => `${prefix}/shortcut`,
        updateAll: () => `${prefix}/shortcut/updateAll`,
    },
    timeTracker: {
        jobs: () => `${prefix}/timeTracker/jobs`,
        startShift: () => `${prefix}/timeTracker/shift/start`,
        endShift: () => `${prefix}/timeTracker/shift/end`,
        startJob: (timesheetId) => `${prefix}/timeTracker/job/${timesheetId}/start`,
        endJob: (timesheetId) => `${prefix}/timeTracker/job/${timesheetId}/end`,
    },
    callLog: {
        byId: (id) => `${prefix}/callLog/${id}`,
        create: () => `${prefix}/callLog`,
        update: (id) => `${prefix}/callLog/${id}`,
        search: () => `${prefix}/callLogs/search`,
        directions: () => `${prefix}/callLogs/directions`,
        dispositions: () => `${prefix}/callLogs/dispositions`,
        jobsByContract: (contractId) => `${prefix}/callLogs/jobsByContract/${contractId}`,
        history: (id) => `${prefix}/callLog/${id}/history`,
        employeesByDispatchLocation: (dispatchLocationId) => `${prefix}/callLog/employeesByDispatchLocation/${dispatchLocationId}`,
        followUpEmployeesByDispatchLocation: (dispatchLocationId) => `${prefix}/callLog/followUpEmployeesByDispatchLocation/${dispatchLocationId}`,
    },
    callDisposition: {
        byId: (id) => `${prefix}/callDisposition/${id}`,
        create: () => `${prefix}/callDisposition`,
        update: (id) => `${prefix}/callDisposition/${id}`,
        delete: (id) => `${prefix}/callDisposition/${id}`,
        search: () => `${prefix}/callDispositions/search`,
        all: (activeOnly = true) => `${prefix}/callDispositions?activeOnly=${activeOnly}`,
        directions: () => `${prefix}/callDispositions/directions`,
    },
    employeeImport: {
        all: (id) => `${prefix}/employeeImport/${id}`,
        import: () => `${prefix}/employeeImport/import`,
        exportADPEmployees: (id) => `${prefix}/employeeImport/exportADPEmployees/${id}`,
    },
    externalEmployee: {
        all: () => `${prefix}/externalEmployee`,
    },
    assetManagement: {
        assetTypes: () => `${prefix}/assetManagement/assetTypes`,
    },
};

export const agGridConstants = {
    columnFilterTypes: {
        number: 'agNumberColumnFilter', // A Number Filter for number comparisons.
        text: 'agTextColumnFilter', // A Text Filter for string comparisons.
        date: 'agDateColumnFilter', // A Date Filter for date comparisons.
        set: 'agSetColumnFilter', // A Set Filter, influenced by how filters work in Microsoft Excel.This is an ag- Grid - Enterprise feature.
    },
};

export const ApplicationPermissions = {

    billing_create: 'billing.create',
    billing_edit: 'billing.edit',
    billing_delete: 'billing.delete',
    billing_view: 'billing.view',
    billing_void: 'billing.void',
    billingissues_view: 'billing_issues.view',

    billingrate_create: 'billingrate.create',
    billingrate_edit: 'billingrate.edit',
    billingrate_view: 'billingrate.view',

    billing_review_view: 'billing_review.view',
    billing_review_accept: 'billing_review.accept',
    billing_review_reject: 'billing_review.reject',
    billing_review_invoice: 'billing_review.invoice',
    billing_tscontract_update: 'timesheet_contract.update',
    billing_charge_force_reject: 'billing_charge_force.reject',
    billing_charge_process: 'billing_charge.process',
    billing_report_view: 'billingreport.view',

    chargetype_create: 'chargetype.create',
    chargetype_edit: 'chargetype.edit',
    chargetype_delete: 'chargetype.delete',
    chargetype_view: 'chargetype.view',

    company_create: 'company.create',
    company_edit: 'company.edit',
    company_delete: 'company.delete',
    company_view: 'company.view',
    company_view_history: 'company.history',
    location_create: 'location.create',
    location_edit: 'location.edit',
    location_delete: 'location.delete',
    location_view: 'location.view',

    locationcontact_create: 'location_contact.create',
    locationcontact_edit: 'location_contact.edit',
    // locationcontact_delete: 'location.delete',
    locationcontact_view: 'location_contact.view',

    contactactivity_create: 'contactactivity.create',
    contactactivity_edit: 'contactactivity.edit',
    contactactivity_delete: 'contactactivity.delete',
    contactactivity_view: 'contactactivity.view',

    contract_create: 'contract.create',
    contract_edit: 'contract.edit',
    contract_delete: 'contract.delete',
    contract_view: 'contract.view',
    contract_export: 'contract.export',
    contract_view_history: 'contract.history',

    contractTemplate_create: 'contract_template.create',
    contractTemplate_edit: 'contract_template.edit',
    contractTemplate_view: 'contract_template.view',
    contractTemplate_delete: 'contract_template.delete',

    customer_create: 'customer.create',
    customer_edit: 'customer.edit',
    customer_contacts_edit: 'customer.contacts_edit',
    customer_delete: 'customer.delete',
    customer_view: 'customer.view',

    dispatch_create: 'dispatch.create',
    dispatch_edit: 'dispatch.edit',
    dispatch_delete: 'dispatch.delete',

    dispatch_employee: 'dispatch_employee.edit',
    dispatch_equipment: 'dispatch_equipment.edit',

    dispatch_view: 'dispatch.view',
    dispatch_view_all: 'dispatch.view_all',

    dispatch_review_view: 'dispatch_review.view',
    dispatch_review_accept: 'dispatch_review.accept',
    dispatch_review_reject: 'dispatch_review.reject',

    dispatch_issues_view: 'dispatch_issues.view',

    dispatch_group_create: 'dispatch_group.create',
    dispatch_group_view: 'dispatch_group.view',
    dispatch_group_edit: 'dispatch_group.edit',
    dispatch_group_delete: 'dispatch_group.delete',

    equipment_create: 'equipment.create',
    equipment_edit: 'equipment.edit',
    equipment_delete: 'equipment.delete',
    equipment_view: 'equipment.view',

    equipmentstatus_create: 'equipmentstatus.create',
    equipmentstatus_edit: 'equipmentstatus.edit',
    equipmentstatus_delete: 'equipmentstatus.delete',
    equipmentstatus_view: 'equipmentstatus.view',

    equipmenttype_create: 'equipmenttype.create',
    equipmenttype_edit: 'equipmenttype.edit',
    equipmenttype_delete: 'equipmenttype.delete',
    equipmenttype_view: 'equipmenttype.view',

    employee_create: 'employee.create',
    employee_edit: 'employee.edit',

    employee_delete: 'employee.delete',
    employee_view: 'employee.view',
    employee_onboarding: 'employee.onboard',
    employee_add: 'employee.add',
    employee_view_history: 'employee.history',

    employee_pay_view: 'employee_pay.view',

    employee_routing_address_edit: 'employee_routing_address.edit',
    employee_home_address_edit: 'employee_home_address.edit',
    employee_username_edit: 'employee_username.edit',
    employee_group_edit: 'employee_groups.edit',
    employee_name_edit: 'employee_name.edit',
    employee_payroll_company_edit: 'employee_payroll_company.edit',
    employee_dispatch_company_edit: 'employee_dispatch_company.edit',
    employee_number_edit: 'employee_number.edit',
    employee_phone_edit: 'employee_phone.edit',
    employee_notifications_edit: 'employee_notifications.edit',
    employee_importupdates_edit: 'employee_importupdates.edit',
    employee_accountstatus_edit: 'employee_accountstatus.edit',
    employee_password_edit: 'employee_password.edit',
    employee_unlock: 'employee.unlock',
    employee_exclude_dispatch: 'employee_exclude_dispatch.edit',

    employee_restriction_view: 'employee_restriction.view',
    employee_restriction_create: 'employee_restriction.create',
    employee_restriction_edit: 'employee_restriction.edit',
    employee_restriction_delete: 'employee_restriction.delete',

    group_create: 'group.create',
    group_edit: 'group.edit',
    group_delete: 'group.delete',
    group_view: 'group.view',

    invoice_view: 'invoice.view',
    invoice_create: 'invoice.create',

    job_create: 'job.create',
    job_edit: 'job.edit',
    job_delete: 'job.delete',
    job_view: 'job.view',
    project_close: 'project.close',
    job_daily_view: 'job_daily_view.view',

    payroll_create: 'payroll.create',
    payroll_edit: 'payroll.edit',
    payroll_delete: 'payroll.delete',
    payroll_view: 'payroll.view',
    payroll_view_individual: 'payroll.viewindividual',

    payroll_review_view: 'payroll_review.view',
    payroll_review_accept: 'payroll_review.accept',
    payroll_review_reject: 'payroll_review.reject',
    payroll_review_export: 'payroll_review.export',
    payroll_review_export_certified_payroll: 'payroll_review.export_certified_payroll',

    self_dispatch_timesheet_create: 'selfdispatchtimesheet.create',
    self_dispatch_timesheet_view: 'selfdispatchtimesheet.view',
    nonFlagging_timesheet_view: 'nonflaggingtimesheet.view',
    nonFlagging_timesheet_create: 'nonflaggingtimesheet.create',

    timesheet_create: 'timesheet.create',
    timesheet_edit: 'timesheet.edit',
    timesheet_delete: 'timesheet.delete',
    timesheet_export: 'timesheet.export',
    timesheet_view: 'timesheet.view',
    timesheet_accept: 'timesheet.accept',
    nf_timesheet_reject: 'timesheet.reject',
    flagging_timesheet_reject: 'flagging_timesheet.reject',
    timesheet_dashboard: 'timesheet_dashboard.view',
    timesheet_edit_approved: 'timesheet_approved.edit',
    timesheet_equipment_audit_logs_view: 'timesheet_equipment_audit_log.view',

    quickBooksInventoryItem_view: 'quickBooksInventoryItem.view',
    quickBooksInventoryItem_create: 'quickBooksInventoryItem.create',
    quickBooksInventoryItem_edit: 'quickBooksInventoryItem.edit',
    quickBooksInventoryItem_delete: 'quickBooksInventoryItem.delete',

    county_group_view: 'county_group.view',
    county_group_create: 'county_group.create',
    county_group_edit: 'county_group.edit',
    county_group_delete: 'county_group.delete',

    jotForm_view: 'jot_form.view',
    jotForm_create: 'jot_form.create',
    jotForm_edit: 'jot_form.edit',
    jotForm_delete: 'jot_form.delete',

    certification_view: 'certification.view',
    certification_create: 'certification.create',
    certification_edit: 'certification.edit',

    equipmentTag_view: 'equipment_tag.view',
    equipmentTag_create: 'equipment_tag.create',
    equipmentTag_edit: 'equipment_tag.edit',

    operationalcategory_view: 'operational_category.view',
    operationalcategory_create: 'operational_category.create',
    operationalcategory_edit: 'operational_category.edit',

    dispatch_plus_view: 'dispatch_plus.view',
    quick_job_create: 'quick_job.create',

    tenant_edit: 'tenant.edit',
    impersonation_toggle: 'impersonation.toggle',
    company_code_edit: 'company_code.edit',
    multi_tenant_all: 'multi_tenant.all',
    supporttool_view: 'supporttool.view',

    // customer portal
    customerportal_all: 'customer_portal.all',
    jobrequest_view: 'job_request.view',
    jobrequest_edit: 'job_request.edit',
    jobrequest_create: 'job_request.create',
    jobrequest_delete: 'job_request.delete',

    // notifications
    notification_dispatch_plus: 'notification.dispatch_plus',

    quickSale_view: 'quick_sale.view',
    quickSale_create: 'quick_sale.create',
    quickSale_edit: 'quick_sale.edit',
    quickSale_delete: 'quick_sale.delete',

    invoice_clear: 'invoice.clear',
    invoice_export: 'invoice.export',
    invoice_view_history: 'invoice.history',

    rental_invoice_view: 'rental_invoice.view',
    rental_invoice_create: 'rental_invoice.create',
    rental_invoice_edit: 'rental_invoice.edit',

    resourceKit_upsert: 'resourceKit.upsert',
    resourceKit_view: 'resourceKit.view',

    callLog_create: 'call_log.create',
    callLog_edit: 'call_log.edit',
    callLog_view: 'call_log.view',

    callDisposition_create: 'call_disposition.create',
    callDisposition_edit: 'call_disposition.edit',
    callDisposition_view: 'call_disposition.view',
    callDisposition_delete: 'call_disposition.delete',

    billinggroup_create: 'billinggroup.create',
    billinggroup_edit: 'billinggroup.edit',
    billinggroup_delete: 'billinggroup.delete',
    billinggroup_view: 'billinggroup.view',

    quick_rental_edit: 'quick_rental.edit',
};

export const Weekdays = [
    {
        id: 0, name: 'Sunday', abbreviation: 'Sun', abbreviationShort: 'S',
    },
    {
        id: 1, name: 'Monday', abbreviation: 'Mon', abbreviationShort: 'M',
    },
    {
        id: 2, name: 'Tuesday', abbreviation: 'Tue', abbreviationShort: 'T',
    },
    {
        id: 3, name: 'Wednesday', abbreviation: 'Wed', abbreviationShort: 'W',
    },
    {
        id: 4, name: 'Thursday', abbreviation: 'Thu', abbreviationShort: 'R',
    },
    {
        id: 5, name: 'Friday', abbreviation: 'Fri', abbreviationShort: 'F',
    },
    {
        id: 6, name: 'Saturday', abbreviation: 'Sat', abbreviationShort: 'S',
    },
];

export const WeekdayAbbreviations = Weekdays.map((day) => day.abbreviation);

export const resourceHasRequestedQuantity = (resource) => Boolean(
    resource
    && (Weekdays.some((day) => Number(resource[day.name.toLowerCase()]) > 0)
        || resource.rentalDropoffQuantity > 0
        || resource.rentalPickupQuantity > 0),
);

export const resourceHasChargeTypeChoice = (resource) => (resource?.chargeTypeOptions?.length ?? 0) > 1;

export const SupportedFileExtensions = {
    image: ['.gif', '.jpg', '.jpeg', '.png', '.heic', '.webp'],
    document: ['.xlsx', '.xls', '.pdf', '.txt', '.doc', '.docx'],
};

export const SupportedFileExtensionString = SupportedFileExtensions.image
    .concat(SupportedFileExtensions.document)
    .join(',');

export const SiteRoles = {
    Administrator: '6f176015-2b18-4d7a-896a-aa97c498ea66',
    RegionalSupport: '4488e0d6-0e54-4079-8b15-39195c23188e',
};

export const ServiceResponseResult = {
    Ok: 0,
    Error: 1,
    OkWithErrors: 2,
};

// Relocated to the shared constants module; re-exported here for backward compatibility.
export { MomentTimeFormat, MomentDateFormat } from '@s20/common/constants/momentFormats';

export const TimesheetReviewType = {
    Client: 1,
    Employee: 2,
};

export const Months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

export const MonthsShort = [
    'Jan',
    'Feb',
    'Ma',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

export const EventStatus = {
    ShiftStart: 'Shift Start',
    JobStart: 'Job Start',
    JobEnd: 'Job End',
    ShiftEnd: 'Shift End',
    NoShow: 'No Show',
};

export const ActivityIcons = {
    [EventStatus.ShiftStart]: faPersonWalkingArrowRight,
    [EventStatus.JobStart]: faUserClock,
    [EventStatus.JobEnd]: faUserCheck,
    [EventStatus.ShiftEnd]: faPersonWalkingArrowLoopLeft,
    [EventStatus.NoShow]: faXmark,
};

export const ContactTypes = {
    Contract: 1,
    Billing: 2,
    Foreman: 3,
    Supervisor: 4,
    Office: 5,
    ProjectManager: 6,
};

export const ChargeTypeApplicabilities = {
    Both: 1,
    PaidOnly: 2,
    BilledOnly: 3,
};

export const ChargeTypeUnits = {
    Hours: 1,
    Flat: 2,
    Percentage: 3,
};

export const ChargeTypeResourceTypes = {
    Employee: 1,
    Equipment: 2,
    None: 3,
    Planning: 4,
    Setup: 5,
    Rental: 6,
    Sale: 8,
};

export const BillingCycles = {
    Weekly: 1,
    Monthly: 2,
    Daily: 3,
};

export const RentalUnitTypes = {
    Hourly: 1,
    Daily: 2,
    Weekly: 3,
    Monthly: 4,
    Each: 5,
    LumpSum: 6,
};

export const CancellationNoteTypes = {
    cancellation: 1,
    auditApplied: 2,
    auditRemoved: 3,
};

export const EmployeeAccountStatuses = {
    All: -1,
    Active: 1,
    NewHire: 3,
};

export const EmployeePropertyDisplayNames = {
    EmployeeRestrictionTypeId: 'Restriction Type',
    RestrictionDays: 'Days',
    RestrictionStartTime: 'Start Time',
    RestrictionEndTime: 'End Time',
    SystemAdded: 'Include with Import Updates',
    USCountyGroupDescription: 'County Group',
    USCountyName: 'County',
    IncentiveAmount: 'Incentive Amount',
    StartDate: 'Start Date',
    EndDate: 'End Date',
    CallDateTimeTimeZoneId: 'Call Time Zone',
};

export const CompanyPropertyDisplayNames = {
    CompanyName: 'Name',
    Active: 'Active',
    CompanyStatusDescription: 'Status',
    Code: 'Code',
    TCCCode: 'TCC Code',
    ExternalId: 'External ID',
    ExternalCode: 'External Code',
    BusinessNumber: 'Business Number',
    OperationCenterCode: 'Operation Center Code',
    Notes: 'Notes',
    IsNationalAccount: 'National Account',
    UseInvoicing: 'Uses Invoicing',
    InvoiceHasPO: 'Invoice Has PO',
    MinimumBillUnits: 'Minimum Bill Units',
    CompanyTypeDescription: 'Company Type',
    CompanyBillingGroupDescription: 'Billing Group',
    ChargeTypeDescription: 'Charge Type',
    ChargeTypeGroupDescription: 'Charge Type Group',
    BillingRateDescription: 'Billing Rate',
    BillingRateAmount: 'Rate',
    PayRate: 'Pay Rate',
    StartDate: 'Start Date',
    EndDate: 'End Date',
    CustomerCompanyName: 'Customer',
    AddressRole: 'Address',
    FirstName: 'First Name',
    LastName: 'Last Name',
    Title: 'Title',
    Email: 'Email',
    MobilePhone: 'Mobile Phone',
    OfficePhone: 'Office Phone',
    MobilePhoneIsPrimary: 'Mobile Phone Is Primary',
    OfficePhoneIsPrimary: 'Office Phone Is Primary',
    ReceiveSmsNotifications: 'Receive SMS Notifications',
    IsActive: 'Active',
    TimesheetEmailRecipients: 'Timesheet Email Recipients',
    IncludeInTimesheetDigests: 'Include in Timesheet Digests',
    ContactFirstName: 'Contact First Name',
    ContactLastName: 'Contact Last Name',
    LocationContactTypeDescription: 'Contact Type',
};

export const InvoicePropertyDisplayNames = {
    InvoiceNumber: 'Invoice Number',
    InvoiceDate: 'Invoice Date',
    AccountingDate: 'Accounting Date',
    InvoiceStatusDescription: 'Status',
    InvoiceAmount: 'Amount',
    CustomerCompanyName: 'Customer',
    BillingCompanyName: 'Billing Company',
    ContractNumber: 'Contract',
    PurchaseOrderNumber: 'PO Number',
    WorkOrderNumber: 'Work Order',
    InvoiceJob: 'Job',
    JobLocation: 'Job Location',
    Memo: 'Memo',
    Terms: 'Terms',
    InvoiceApprovedOn: 'Approved On',
    InvoiceSentOn: 'Sent On',
    InvoicePaidOn: 'Paid On',
    InvoiceEmailDate: 'Emailed On',
    ExportRevised: 'Export Revised',
    InvoiceVersion: 'Version',
    DeletedOn: 'Deleted On',
    TimesheetNumber: 'Timesheet',
    JobDate: 'Job Date',
    ForemanName: 'Foreman',
    CrewLeadName: 'Crew Lead',
    DispatchLocationName: 'Dispatch Location',
    IsCancel: 'Cancellation',
    RateTypeDescription: 'Rate Type',
    CalcBillQty: 'Calculated Quantity',
    BillQty: 'Billed Quantity',
    Usage: 'Usage',
    BillUsage: 'Billed Usage',
    VarianceQty: 'Quantity Variance',
    CalcBillAmount: 'Calculated Amount',
    BillAmount: 'Billed Amount',
    VarianceAmount: 'Amount Variance',
    Current: 'Current',
    IsCredit: 'Credit',
    ExportNumber: 'Export Number',
};

export const ContractPropertyDisplayNames = {
    ContractStatusDescription: 'Contract Status',
    CustomerCompanyName: 'Customer',
    BillingCompanyName: 'Billing Company',
    EndCompanyName: 'End Customer',
    OperationsCenterCompanyName: 'Operations Center',
    BillingContactName: 'Billing Contact',
    ContractScheduleTypeDescription: 'Contract Schedule Type',
    BillingContractScheduleTypeDescription: 'Billing Contract Schedule Type',
    ContractUnionWorkTypeDescription: 'Union Work Type',
    WorkOrderTypeDescription: 'Work Order Type',
    TimesheetResponsibilityTypeName: 'Timesheet Responsibility',
    InvoiceTermsDescription: 'Invoice Terms',
    InvoiceMappingName: 'Invoice Mapping',
    CertifiedPayrollFormDescription: 'Certified Payroll Form',
    BillingCycleDescription: 'Billing Cycle',
    WorkCardTypeDescription: 'Work Card Type',
    OperationalCategoryName: 'Operational Category',
    HasSundayDT: 'Has Sunday Double Time',
    ECMSNumber: 'ECMS Number',
    PWProjectNumber: 'PW Project Number',
    RequireGLString: 'Require GL String',
    ChargeTypeDescription: 'Charge Type',
    ChargeTypeEligibilityDescription: 'Eligibility',
    LocationName: 'Location Name',
    LocationAddress: 'Address',
};
